// compute.ts — Engine assembly: from a quote state, produces the priced lines, the material aggregates,
// the minima, the totals and the blockers, orchestrating the themed modules.

import { pvComponents, splitPv, fullCombination, lineContainsCombination } from "../catalogue/base-price";
import { ACTIVE_CATALOGUE, type Catalogue } from "../catalogue/catalogue-source";
import { itemAllowed } from "../catalogue/compatibility";
import {
  type QuoteLine,
  type ForcedMaterial,
  num,
  round2,
  round3,
  isFilled,
  findMat,
  findAccessory,
  findStand,
  materialInFinishing,
  unitM2,
  compositionSnapshot,
} from "../core/core";
import { engineMessage, messageParam, messageListParam } from "../core/messages";
import {
  isTierRegime,
  isTransferRegime,
  isUnitRegime,
  isJoineryFabRegime,
  isLargeFormatPrintRegime,
  isCustomPrintRegime,
  isAccessoryMlRegime,
  isServiceRegime,
} from "../core/regimes";
import type { BackPlanning } from "../ops/back-planning";
import {
  checkOverflow,
  effectiveSplice,
  parseFormat,
  formatAuto,
  ZERO_MARGINS,
  neutralizedMargins,
  SPLICE_NOTICE,
  SUPPLY_NOTE,
  SPLICE_NOTE,
  type TechnicalMargins,
} from "../ops/material-format";
import type { MaterialSupplyRef } from "../ops/material-sourcing";
import { computeAncillaryCosts, type AncillaryCost, type AncillaryCostsResult } from "../pricing/ancillary-costs";
import { allocateDiscount, effectiveRate, type AllocationEntry, type SubPaEntry } from "../pricing/discount-allocation";
import { lineDiscount, generalDiscountRate, generalDiscountAmount, rush, type RushResult } from "../pricing/discounts";
import {
  needFromPieces,
  finalCost,
  allocateProRata,
  materialBalance,
  type MaterialNeed,
  type PurchasePiece,
  type MaterialBalance,
} from "../pricing/material-purchase";
import {
  elementFloor,
  catalogueFloor,
  defaultUnitPrice,
  materialFlatFee,
  triggerFlatFee,
  componentFlatFees,
} from "../pricing/minima";
import {
  targetPaM2 as paTargetM2Fn,
  targetPaTotal,
  marginPct,
  flatFeePa,
  effectiveDiscount,
  detectCombi,
  paMode,
  paSupplierLookup,
  paGroupKeyP8,
  p8OneM2MinimumFactor,
  groupMinimum,
  type GroupCumulation,
  servicePaTotal,
  type PaMode,
  type PaRateCards,
  EMBEDDED_PA_GRIDS,
  negotiatedFromTotal,
} from "../pricing/pa-margins";
import { materialPa, isUpz, materialNatureOf, hasUnknownMaterialGroup, type MaterialPa, type MaterialPaSource } from "../pricing/pa-material";
import {
  resolveTargetPrice,
  quoteFloor,
  refusalMessage,
  type GeneralDiscountMode,
  type RushTarget,
  type TargetEntry,
} from "../pricing/target-discount";
import {
  cumulateByCategory,
  marginTier,
  targetMarginForTier,
  assertVolumeGrid,
  type VolumeGrid,
} from "../pricing/volume-tiers";
import { carpetPriceM2, findMoq, moqPriceM2, moqCutSurcharge } from "../products/carpets";
import { priceCustomSize, inheritedRate } from "../products/custom-size";
import { extraOf, isCustomSizeSurcharge } from "../products/custom-size-line";
import { validateCustomSize } from "../products/custom-size-validation";
import { computeServices, type ServicesBlock, type Counters, type ServiceResult } from "../products/services-pricing";
import {
  standFamily,
  cumulateByFamily,
  standPrice,
  transferPrice,
  standArea,
  standLinePrintedM2,
  customSizeRateForStand,
  isCompositionModified,
  tierUnitPrice,
  tierPa,
  type StandPrice,
} from "../products/stands";
import { autoMeterage, svcCutPvRate, svcCutPaRate, sleeveWithoutDiameter, displayNumMap } from "../products/triggers";
import {
  normalizeClientRateCards,
  validateEntries,
  resolveLineEntries,
  resolveEntryRef,
  resolveEntryTier,
  entryThresholds,
  tierIndex,
  effectiveBase,
  composeRate,
  activeEntry,
  type RateCardEntry,
  type ClientRateCard,
  type DedicatedClientRateCard,
  type ResolvedTier,
} from "../rate-cards/client-rate-cards";
import type { PaymentTerms } from "../rate-cards/payment-terms";
import {
  CLIENTS,
  RUSH_OPTS,
  SERVICE_KEY,
  BASE_MARGIN,
  DEFAULT_MAT_FORMAT,
  MAT_FORMATS,
  MOQ_TIERS,
  STANDS,
  FINISHED_PRODUCTS,
  MATERIAL_PA_SOURCE,
  assertEngineDataBound,
  engineSettings,
} from "../../data";

/** Quote header fields (top banner). No pricing impact. */
export interface QuoteHeader {
  opp?: string;
  folder?: string;
  contact?: string;
  salesRep?: string;
  projectManager?: string;
  /** Payment terms, seeded from the client record and editable here. Frozen with the quote. No pricing effect. */
  paymentTerms?: PaymentTerms;
  /**
   * Back-planning: the two upstream dates (file reception and proof approval). Frozen with the quote.
   * The delivery date is NOT here — it lives outside the engine state, in the quote record (single source of truth).
   */
  backPlanning?: BackPlanning;
  /**
   * Free comment printed on the Sellsy PDF under the product lines only. Empty / whitespace → omitted from the PDF.
   * Frozen with the quote. No pricing effect.
   */
  pdfComment?: string;
}

/** Quote "dispatch" sorting column (grouping by zone for packaging) — UI only, no pricing. */
export interface DispatchCol {
  /** Stable identifier (key of the QuoteLine.dispatch values), independent from the displayed name. */
  id: string;
  /** Displayed column name (auto "dispatch N" on creation, freely editable). */
  name: string;
}

/** Quote state fed into the engine. */
export interface QuoteState {
  /** Client index (CLIENTS) — carries the client discount (legacy; superseded by agencyDiscount). */
  clientIdx?: number;
  /** Agency discount as a 0..1 fraction (1st discount of the cascade). UI input is capped 0–16 %, but the
   *  engine compounds any rate. Takes precedence over clientIdx when set. */
  agencyDiscount?: number;
  /** The dedicated client rate table (§19): threshold set + unified entries (base ⊗ scale, client minima,
   *  showcase). The legacy shape (array of the three historical mechanisms) is still accepted — saved quote
   *  states carry it — and is migrated on the fly, to the cent. */
  clientGrids?: DedicatedClientRateCard | readonly ClientRateCard[];
  /** Id of the selected client record (traceability; restores the picker). book §19. */
  clientId?: string;
  /** Quote header fields: OPP, folder, contact, sales rep, project manager. */
  header?: QuoteHeader;
  /** Highest internal line id ever given in this quote (and the quotes it was copied from): ids are never reused, a
   *  deleted line's id never comes back on a new line. No pricing effect. */
  lastLineId?: number;
  /** Validation workflow status, a `QuoteStatus` value: draft / pending / validated / refused / to_fix / sent. */
  validationStatus?: string;
  /** General discount entered as a % (capped 0–100). */
  discountGen?: number | string;
  /**
   * Input mode of the general discount (book 4.10 §5). `rate` = type a percentage. `target` = type the
   * wanted final total (rush included) and the amount is derived from it. Absent = `rate`.
   */
  discountGenMode?: GeneralDiscountMode;
  /** `target` mode: the targeted final total excl. VAT, rush included. */
  discountGenTarget?: number | string | null;
  /**
   * `target` mode: the amount frozen when the target was set. Once set it never moves on its own — a line
   * added afterwards is full price and the total drifts above the target. The "recalibrate" button
   * re-resolves it. In `rate` mode nothing is ever frozen.
   */
  frozenGeneralDiscountAmount?: number | string | null;
  /** Free-text discount label, echoed on the banner and the client document. */
  discountGenLabel?: string;
  /**
   * Timestamp + author of the last human pricing decision on the general discount (set a target,
   * recalibrate it, change the rate, take a line out of the base). Written by the application, never by a
   * database trigger.
   */
  discountGenModifiedAt?: string;
  discountGenModifiedBy?: string;
  /** "Apply the discount to the minimum flat fee and minimum unit price" checkbox. */
  includeMinima?: boolean;
  /** Number of finishing columns displayed in the grid (2..6). UI metadata: the engine always sums
   *  finishing1..finishing6 whatever the visible column count. Defaults to 2. No pricing impact. */
  finishingColumns?: number;
  /** "Dispatch" sorting columns (grouping by zone for packaging). UI metadata with no pricing impact: the
   *  engine never reads them. Each column carries a stable id plus an editable name; the per-line value
   *  lives in QuoteLine.dispatch[id]. */
  dispatchCols?: readonly DispatchCol[];
  lines: readonly QuoteLine[];
  /** Admin overrides per material (forced PV / minimum unit price). */
  forcedMaterials?: Record<string, ForcedMaterial>;
  /** Forced purchase price of a per-material minimum flat fee. */
  forcedMinFeePa?: Record<string, number | string>;
  /** Actual supplier purchases (in-house UPZ stock / free lines), by category key. */
  materialPurchases?: Record<string, { pricePerM2?: string | number; supplier?: string }>;
  /** Quote services (artwork, packaging, logistics, installation, rental) + rush. */
  services?: ServicesBlock & { rush?: string; rushNego?: number | string; rushPa?: number | string };
  /** Extra purchase fees (book 4.9): purchases with no sale counterpart — margin impact only
   *  (total purchase + final margin), zero PV, outside every discount base. */
  ancillaryCosts?: readonly AncillaryCost[];
}

/** Priced output line. */
export interface PricedLine {
  id: number | undefined;
  type: QuoteLine["type"];
  /** Area (m²) rounded to 3 decimals. */
  m2: number;
  /** Total quantity (files × copies). */
  quantity: number;
  pvM2: number | null;
  /** Public PV €/m² (full, before any discount) — 1st cascade column, breakdown tooltip. */
  pvPublic?: number;
  /** Net tier PV €/m² (engine forecast = full × (1 − composed agency⊗tier discount)), always computed even
   *  on a forced line: cascade columns stay an engine forecast. null under a rate table. */
  tierNetPv?: number | null;
  pu: number | null;
  theoreticalUnitPrice: number;
  /** Catalogue minimum unit price floor (€/piece, line override excluded) — read-only auto column.
   *  Purely indicative: the retained unit price (`pu`) already includes any override. */
  catalogueMinUnitPrice?: number | null;
  total: number;
  pvCalc: number | null;
  matRef: string | null;
  combi: string;
  forcedLine: boolean;
  forcedMat: boolean;
  floorApplied: boolean;
  flatFeeReplaced: boolean;
  targetPa: number;
  targetPaM2: number | null;
  margin: number;
  /** Effective line discount as a 0..1 fraction: agency + tier cascade composed (60 % cap), or the
   *  discount inherited from the link. Feeds the composed "Rem. %" column. See also forcedLineDiscount. */
  discountPct: number;
  /**
   * Share of the general discount conceded by this line, in € (target mode, prorated on price). Internal
   * use — per-line margin, future BCF, displayed effective discount. The line price is not modified.
   */
  discountGenConceded?: number;
  /** Effective general discount rate of the line (concession / total). Uniform across the whole base. */
  discountGenRateEff?: number;
  // Agency -> tier cascade (book 4.5 §4), exposed per line.
  /** Agency discount (client record), 0..1 fraction — 1st cascade discount, on the catalogue PV. */
  agencyDisc?: number;
  /** Net PV €/m² after the agency discount, before the tier (rounded to 2 decimals for display). */
  pvNetAg?: number | null;
  /** Volume tier rate alone (0..1 fraction), displayed separately from the composed one. */
  tierRate?: number;
  /** Target margin of the active tier, in % — always filled on a priced print line. */
  targetMarginTier?: number | null;
  /** Target margin in €/m² = net PV €/m² × tier target rate. Always filled (reference, cost-independent). */
  marginTargetM2?: number | null;
  /** Effective margin in €/m² = (net PV − retained purchase price) / m². Only on a real PA, else null. */
  effectiveMarginM2?: number | null;
  /** Real margin strictly below the tier target → non-blocking orange alert. */
  marginBelowTarget?: boolean;
  /** €-tier rate table entry served on the line (§19): the client price is taken as-is → badge on
   *  PV €/m², cells locked. value = retained price. */
  clientGrid?: { id: string; mode: "net_price"; value: number };
  /** Base of the served entry (§19) → badge on the agency discount column (the tooltip details the
   *  partial material + printing base for a material entry). */
  agencyGrid?: { id: string; rate: number; targetType: "item" | "combination"; targetRef: string; name?: string };
  /** Scale rate of the served entry's active tier → badge on the tier discount column. */
  tierGrid?: { id: string; rate: number; targetType: "item" | "combination"; targetRef: string; name?: string };
  /** Per-component breakdown of a line under entries (§19) — the public PV tooltip: each component, its
   *  rate, the entry that served it (empty id = standard rate). */
  gridBreakdown?: { role: string; code: string; rate: number; entryId: string }[];
  /** Negotiated price (prix_net table) strictly below the catalogue minimum unit price → visual alert
   *  (floors are not applied on a net price, but we flag it). */
  belowMinUnitPrice?: boolean;
  /** Carpet line (dedicated regime). */
  isMoq?: boolean;
  /** Service line (hourly cutting). */
  isSvc?: boolean;
  /** Custom-size print. */
  customPrint?: boolean;
  /** Off-catalogue (free) line. */
  offCatalogue?: boolean;
  /** Free name (off-catalogue / custom-size line). */
  name?: string;
  /** Missing purchase price (blocking). */
  paMissing?: boolean;
  /** Effective discount of a forced-price line (gap to the full price). */
  forcedLineDiscount?: number | null;
  /** Stale override: components changed since the price was forced. */
  forceStale?: boolean;
  /** Missing mandatory fields (material/print/cut/L/H). */
  missingFields?: string[];
  /** Items incompatible with the material (§17 gate check): the line exists but is NOT priced until the
   *  offending item is picked again. `field` = UI column. */
  incompatibleFields?: { field: string; ref: string }[];
  // Actual supplier purchase price
  paMode?: PaMode;
  servicePaM2?: number | null;
  paServiceTot?: number | null;
  paMini1m2?: boolean;
  actualMargin?: number | null;
  /** Net PV of the line (total after the general discount) — denominator of the real margin, used for the
   *  "target margin → negotiated purchase price" conversion in the UI. Print m² only. */
  lineNetPv?: number | null;
  /** UPZ material purchase price: gross/coeff/net + nature + missing. The net feeds the margin; the
   *  €/m² workshop purchase price is then print+cutting only (no double counting). isUpz=false → "—". */
  matPa?: MaterialPa;
  /** UPZ line whose gross material purchase price is missing (neither entered nor in the reference data):
   *  soft signal, real margin not computed (never inflated). A missing gross value is also a UPZ validation
   *  blocker (`upzBlockers.gross`) when the supply reference data is injected (supabase mode). */
  paMatMissing?: boolean;
  /** Per-field UPZ validation blockers. Set only on an in-house stock line and when the supply reference
   *  data is injected (supabase mode, see ComputeOptions.materialSupplyRef). Each true flag = missing
   *  mandatory field → red cell in the UI + entry in the red banner. In degraded (embedded) mode no
   *  `upzBlockers` is set and the freeze-time guard takes over. */
  upzBlockers?: {
    /** Distributor (material supplier) missing: not on the line, not starred in the reference data, no actual purchase. */
    distributor: boolean;
    /** Brand (manufacturer) missing: not on the line, not starred in the reference data. */
    brand: boolean;
    /** Gross material purchase price €/m² missing: not entered, not in reference/base data, no actual purchase. */
    gross: boolean;
    /** No workshop selected on the UPZ line. */
    workshop: boolean;
    /** Workshop selected but no purchase rate table supplied for the key, and no manual negotiated price. */
    workmanship: boolean;
    /** Label of the missing workshop key (for the banner message), when `workmanship` is true. */
    processKey?: string;
  };
  /** Negotiated purchase price (entered by hand, `manualPaM2`): "negotiated" regime of the banner. */
  paNegotiated?: boolean;
  // Purchase price broken down into 3 side-by-side regimes: target / rate table / negotiated.
  // Each regime carries its €/m² rate, its €/unit purchase price and its margin % (vs the line net PV).
  // Display only — never a PV computation. The retained regime (negotiated > rate table > target) stays on
  // servicePaM2/paServiceTot/actualMargin + paNegotiated. The €/m² rates are gross (the workshop minimum is a
  // group-level adjustment, flagged separately), so a rate never blows up on a small piece.
  /** Pure target purchase price €/m² — target formula (full × (1−tier) × (1−target margin)), gross rate,
   *  independent of any negotiated/rate-table value (unlike targetPaM2, the retained one). Target column. */
  pureTargetPaM2?: number | null;
  /** Pure target purchase price €/unit (= pureTargetPaM2 × unit area). Target margin % = targetMarginTier. */
  targetPaU?: number | null;
  /** Rate-table purchase price €/m² — workshop rate, always exposed (even when a negotiated one covers it). */
  gridPaM2?: number | null;
  /** Rate-table purchase price €/unit (= gridPaM2 × unit area). */
  gridPaU?: number | null;
  /** Margin % on the rate-table purchase price (vs line net PV); null without a rate table. */
  gridMarginPct?: number | null;
  /** Negotiated purchase price €/m² (= manualPaM2); null without one. */
  negotiatedPaM2?: number | null;
  /** Negotiated purchase price €/unit (= negotiatedPaM2 × unit area). */
  negotiatedPaU?: number | null;
  /** Negotiated purchase price of the whole line (= negotiatedPaM2 × the line's m²). */
  negotiatedPaTotal?: number | null;
  /** Margin % on the negotiated purchase price (vs line net PV); null without one. */
  negotiatedMarginPct?: number | null;
  // Material format: oversize (seams) & supply (format-mat.ts).
  /** Format retained for the line (l.format || the material's ★ default); "—" if the material has none. */
  formatRetained?: string;
  /** true when the retained format is NOT the material's ★ default → orange cell. */
  formatNotDefault?: boolean;
  /** An oversize test actually took place (known format + dimensions filled in). */
  formatTested?: boolean;
  /** Unit dimensions exceed the retained format (margins included) → auto seam checkbox + lock. */
  exceedsFormat?: boolean;
  /** Effective seam = the « raccord » box, ticked by default on oversize. Carries the client note + production summary. */
  spliced?: boolean;
  /** "Specific web width to supply" (echo of l.specificSupply) — production summary only, never the client. */
  specificSupply?: boolean;
  /** The retained format comes from the automatic choice (no format set by hand). Informs the tooltip. */
  formatAuto?: boolean;
  /**
   * Derived production notes: specific format to supply and/or several seamed pieces. They address the
   * workshop, unlike `clientNotice` which goes to the client, and they are derived: `l.prodNote` stays
   * the free human input and is never overwritten.
   */
  productionNotes?: string[];
  /** Fixed client note (seamed delivery) when a seam applies — export + Sellsy. */
  clientNotice?: string;
  // internals (prefixed to distinguish them from public fields)
  _catKey?: string;
  _basePv?: number;
  _fullPv?: number;
  _totalDiscount?: number;
  /** Discount the purchase price "sees": the tier alone (never the agency). PA is client-invariant (book 4.5). */
  _paDiscount?: number;
  /** Displayed purchase price of the line, € — exactly what the cell shows (negotiated/real if retained,
   *  else target). Set by `computeMarginCoverage`, the single derivation; read by the negative-margin
   *  guard of the general discount. `undefined` = line cannot be priced, we assert nothing about it. */
  _displayedPa?: number;
  /** Target margin of the active tier as a fraction (0..1) — source of the public targetMarginTier field. */
  _targetMarginTierFrac?: number;
  _floorTotal?: number;
  _paGross?: number;
  /** UPZ: total workshop purchase price (print+cutting only), material excluded — shown in the €/m² and
   *  €/unit columns. _paGross of a UPZ line = _workshopPaTot + net material; the margin uses that total. */
  _workshopPaTot?: number;
  /** UPZ: true when the workshop purchase price comes from a rate table (real price), else target fallback. */
  _paWorkshopIsActual?: boolean;
  /**
   * UPZ: share of the actual purchase cost falling to this line (€), when a purchased material is entered.
   * Absent = no usable purchase, the line keeps the reference estimate. Present, it replaces it — never
   * adds to it (convergence, never two truths).
   */
  _purchaseShare?: number;
  /** Minimum purchase flat fee of the key (UPZ: read in Pass 2, where the supplied rate table is consulted). */
  _paMinFlatFee?: number;
  /** Workshop of the line — read in Pass A to find the workshop minimum in Pass B. */
  _paWorkshop?: string;
  /**
   * Minimum purchase flat fee applied to the group (€) — tooltip "purchase price at workshop minimum".
   * The purchase price stays black: it is a real price, not an estimate.
   */
  paFlatFeeWorkshop?: number | null;
  /** true when a purchase minimum (m² or €) raised the group's purchase price. */
  paMinApplied?: boolean;
  _pvNet?: number;
  _paGroup?: string;
  _basePvU?: number;
  _paStand?: number;
  /**
   * Custom-size extra (§9) — the readout of its pricing, so the screen displays instead of
   * recomputing; a second arithmetic in the UI would eventually diverge from the quote unnoticed.
   */
  customSize?: {
    actualDeltaM2: number;
    billedDeltaM2: number;
    minSurfaceApplied: boolean;
    appliedFloorPrice: boolean;
    rateRef: string;
    ratePvM2: number;
    base: { L: number; H: number };
  };

  standRegime?: "pack" | "composed";
  _tierRate?: number;
  _linkDiscount?: number;
  /** Target margin (fraction) inherited from the parent for a linked linear-metre accessory. */
  _linkTargetMargin?: number;
  _basePvLink?: number;
  _svcCumulH?: number | null;
}

/** Aggregate per cumulation category (material). */
export interface MaterialAggregate {
  ref: string;
  name: string;
  m2: number;
  total: number;
  /** Effective minimum flat fee of the material (€) = material share + distinct component fees (R-DP-1). */
  minFlatFee: number;
  /** Material share of the fee (base + backing), components excluded — for the summary tooltip. */
  materialFlatFee: number;
  /** Consolidated distinct component fees (once each, R-DP-1) — tooltip detail. */
  componentFlatFees: { code: string; fee: number }[];
  stockUpz: boolean;
  minTriggered: boolean;
}

/** Minimum flat fee line (replaces the lines of a material sitting below its fee). */
export interface FlatFeeLine {
  ref: string;
  name: string;
  amount: number;
  /** "Computed" total of the material (sum of the lines before the switch) — "computed → retained" tooltip. */
  computedTotal: number;
  /** Material share of the fee (€) — summary tooltip: "material share + per-component share" (R-DP-1). */
  materialFlatFee: number;
  /** Distinct component fees (€) making up the flat fee — tooltip detail (R-DP-1). */
  componentFlatFees: { code: string; fee: number }[];
  /** Target purchase price of the fee (percentage of the flat amount) — kept for the tooltip. */
  targetPa: number;
  /** Aggregated cost of the replaced lines, at their real area — kept for the same tooltip. */
  actualCost: number;
  /**
   * Retained purchase price of the fee — the figure that enters the quote's total purchase price.
   * Neither `targetPa` nor `actualCost`: see `computeFlatFeePa` for the two branches and their rationale.
   */
  paRetained: number;
  /** Where `paRetained` comes from: "grid" = observed cost; "target" = targeted cost, for lack of a rate table. */
  paOrigin: "grid" | "target";
}

/** Margin coverage of the quote — aggregated over the lines with a real purchase price only. */
export interface MarginCoverage {
  /** Print lines with a real purchase price (workshop rate table / negotiated price / complete UPZ). */
  realLineCount: number;
  /** Print lines that have a margin notion (target defined) — denominator of the coverage. */
  printLineCount: number;
  /** Sum of net PV of the real-purchase-price lines + services PV (base of the coverage margin %). */
  actualNetPv: number;
  /** Sum of retained purchase price of the real lines + services purchase price. */
  actualPa: number;
  /** Target margin weighted by net PV (all print lines having a target), 0..1 fraction; null if no PV. */
  weightedTargetMarginFrac: number | null;
  /**
   * Displayed total purchase price: sum of the purchase price each line shows (negotiated if entered, else
   * real, else target) + services purchase price. Honest sum, never 0 when costs are at target, so the
   * banner and the summary say the same thing as the lines. Margin € = totalFinal − paTotal.
   */
  paTotal: number;
  /** Breakdown of the displayed purchase price per regime (print lines). Services sit in `actual.pa`. */
  breakdown: {
    negotiated: { n: number; pa: number };
    actual: { n: number; pa: number };
    target: { n: number; pa: number };
  };
}

/**
 * Visual purchase-price/margin state of a line (book §21, §27.2), driving the colour map:
 * "actual" = green (real values from the rate tables), "target" = dotted blue (target values, no rate table
 * or incomplete UPZ), "manual" = orange (hand-edited). "Real" means effectiveMarginM2 is non-null, so a UPZ line
 * missing any component falls back entirely to target — never partially green.
 */
export type PaVisualState = "actual" | "target" | "manual";

/**
 * Visual purchase-price/margin state of a line.
 * @param row The priced line (only its effective margin is read).
 * @param manual Whether the purchase price was hand-edited.
 * @returns "manual" when hand-edited, "actual" when a real margin exists, else "target".
 */

/**
 * Negotiated purchase price per m² of a print line: the typed €/m², else its negotiated TOTAL spread over the line's
 * m². Null while neither is usable (no total, or no size yet).
 * @param l - The line.
 * @param m2 - Total m² of the line.
 * @returns The €/m², or null.
 */
function manualPaM2Of(l: QuoteLine, m2: number): number | null {
  if (isFilled(l.manualPaM2)) return num(l.manualPaM2);
  if (isFilled(l.manualPaTotal) && m2 > 0) return num(l.manualPaTotal) / m2;
  return null;
}

export function paVisualState(row: Pick<PricedLine, "effectiveMarginM2">, manual: boolean): PaVisualState {
  if (manual) return "manual";
  return row.effectiveMarginM2 != null ? "actual" : "target";
}

/**
 * Should the €/m² indicators of a line be hidden (cells shown as "—")? True as soon as the line price comes
 * from a minimum rather than from the area: per-m² indicators divided by a tiny area become meaningless.
 * Display only — the computation, the €/unit purchase price, the margin € and % and the visual regime stay.
 * @param row The priced line (only its minimum flags are read).
 * @returns True when the €/m² indicators must be hidden.
 */
export function hiddenM2Indicator(row: Pick<PricedLine, "floorApplied" | "flatFeeReplaced">): boolean {
  return !!row.floorApplied || !!row.flatFeeReplaced;
}

/**
 * Everything the target-price mode produces — banner, submission summary, guards.
 * Absent when the general discount is in rate mode: that mode changes nothing.
 */
export interface GeneralDiscountTargetInfo {
  /** The requested target: final total excl. VAT, rush included. */
  target: number;
  /** Free-text label entered with the discount. */
  label?: string;
  /** When, and by whom, the discount was last decided. */
  modifiedAt?: string;
  modifiedBy?: string;
  /** Frozen amount applied. Null until a target has been set. */
  amountFrozen: number | null;
  /**
   * What the target would require now, on the current base. Compared to the frozen amount it reveals the
   * drift — and it is what "recalibrate on the target" will freeze in turn.
   */
  recalibratedAmount: number | null;
  /** Resolution refusal, business message ready to display (target too high / below the floor). */
  refusal?: string;
  /** Absolute floor of the quote — the lowest reachable total without lifting the minima protection. */
  floorAmount: number;
  /**
   * What is needed to resolve another target without waiting for a compute round: when the user types a
   * price the UI must freeze the amount in the same gesture, and it calls `resolveTargetPrice` itself.
   */
  totalBeforeDiscount: number;
  maxDiscount: number;
  rush?: RushTarget;
  /** Gap between the target and the real final total. Zero while nothing moved since it was set. */
  gap: number;
  /** Why the total drifted from the target — empty while it has not. */
  driftReason?: string;
  /** The total landed one cent beside the target, through rush rounding (see `target-discount`). */
  rushRounded?: boolean;
  /** Lines whose discounted total falls below their purchase price, named with their loss. Never blocking alone. */
  belowPa: SubPaEntry[];
  /** Number of lines taken out of the base by hand. */
  excludedCount: number;
}

/** What a genuinely purchased UPZ material returns to the quote — read by the bottom-of-screen block. */
export interface MaterialPurchaseResult {
  need: MaterialNeed;
  /**
   * Invoiced cost of the material for this quote (retained quantity × unit × price). `null` while the
   * price is not entered: the row exists so it can be filled, but it weighs nothing and the lines keep
   * the reference estimate.
   */
  cost: number | null;
  /** What is left: remainder in linear metres (flexible) or sheet balance (rigid). */
  balance: MaterialBalance | null;
  supplier: string | null;
}

/** Result of a quote computation. */
export interface QuoteResult {
  lines: PricedLine[];
  materials: Record<string, MaterialAggregate>;
  flatFeeLines: FlatFeeLine[];
  /**
   * Actual UPZ material purchases, by material ref — absent while none is usable. Entries stack up:
   * one per material consumed by the quote.
   */
  materialPurchaseResults?: Record<string, MaterialPurchaseResult>;
  totalPrint: number;
  totalBeforeDiscount: number;
  generalDiscountAmountVal: number;
  /** General discount in target-price mode (book 4.10 §5) — absent in rate mode. */
  generalDiscountTargetInfo?: GeneralDiscountTargetInfo;
  /**
   * Quote margin: (final total − total purchase price) / final total, in %. `null` on a worthless quote.
   * This is what the two thresholds watch — never a single line's margin, which can dive without the
   * quote being at risk.
   */
  quoteMarginPct?: number | null;
  /** Quote margin below the target but above the floor: visible alert, never blocking. */
  marginAlert?: string;
  totalFinal: number;
  /** Total public PV (before discounts) — bottom banner. */
  totalPvPublic: number;
  totalTargetPa: number;
  /** Margin coverage: aggregates computed on the real-purchase-price lines only — the target is never
   *  counted as real. The banner and the summary show the margin on those lines plus the coverage rate. */
  marginCoverage: MarginCoverage;
  rush: RushResult;
  blockers: string[];
  serviceItem: ServiceResult;
  /** Extra purchase fees (book 4.9): valid entries + total — total purchase price and final margin only
   *  (the sale total, the discount bases and the material summary never see them). */
  ancillaryCosts: AncillaryCostsResult;
  fileCount: number;
  elementCount: number;
  totalM2: number;
  totalWeight: number;
}

/** i18n key of the name of each line field, for the blocker messages (French lives in fr.json). */
const FIELD_LABEL_KEY: Record<string, string> = {
  material: "engineFieldMaterial",
  print: "engineFieldPrint",
  cut: "engineFieldCut",
  lamination: "engineFieldLamination",
  finishing1: "engineFieldFinishing1",
  finishing2: "engineFieldFinishing2",
  L: "engineFieldWidth",
  H: "engineFieldHeight",
};

/**
 * A line field as a message-list item: its translated name when known, else the raw code.
 * @param field The field code.
 * @returns The key of its name (encoded message), or the code itself.
 */
const fieldItem = (field: string): string => FIELD_LABEL_KEY[field] ?? field;

/** Compute options. `compatCheck`: applies the §17 gate check — a line holding an item forbidden for
 *  its material is NOT priced (incompatibleFields + blocker). Off by default. */
export interface ComputeOptions {
  compatCheck?: boolean;
  /**
   * Injected catalogue (price-bearing tables). Absent = the engine's embedded catalogue. The application
   * passes the database version so that a price change reaches the drafts.
   */
  catalogue?: Catalogue;
  /**
   * Injected purchase rate tables (workshop × key). Absent = embedded. A purchase tier change then
   * reaches the drafts, never a validated quote.
   */
  paGrids?: PaRateCards;
  /**
   * Source of the material purchase price (gross + waste coefficients per material and per family).
   * Absent = embedded reference data + per-nature defaults.
   */
  sourcePaMaterial?: MaterialPaSource;
  /**
   * Batch pricing (set by `computeLot`): the quote-level mandatory services block (artwork, packaging,
   * delivery) belongs to the whole quote, so its blockers are not raised for a batch of lines.
   */
  batch?: boolean;
  /**
   * Volume and turnover already acquired by the contractual reference, per cumulation category. Injected
   * input, so the engine stays pure: `m2` adds to the category cumulation, so sale and purchase tiers are
   * read on the [reference + computed lines] pool; `euros` adds to the trigger base of the material
   * minimum flat fee. Absent = original behaviour, to the byte.
   */
  referenceOffsets?: Record<string, { m2: number; euros: number }>;
  /**
   * Quote margin thresholds, injected from `app_settings` like the rate table and the catalogue.
   * Absent = no guard at all. Below `targetPct` a visible, non-blocking alert; below `floorPct` a
   * blocker, the supervisor gate of the freeze.
   */
  marginThresholds?: { targetPct: number; floorPct: number };
  /**
   * Technical margins (web width / sheet / reel) for the material format oversize test. Injected like the
   * catalogue. Absent = the margins bound from `app_settings` with the engine data. A change reaches the drafts on recompute, never a frozen quote.
   */
  margins?: TechnicalMargins;
  /**
   * Material supply reference data (distributor + brand per material). Injected for the sole evaluation of
   * the UPZ validation blockers: the purchase-price mode stays decoupled (supplier/brand never weighs on
   * the price). Present and non-empty = supabase mode, per-line UPZ blockers are evaluated. Absent or
   * empty = per-line evaluation disabled, the freeze-time guard takes over on the app side.
   */
  materialSupplyRef?: MaterialSupplyRef;
}

/**
 * Computes a full quote from its state. Pure function: reads only the given state and the catalogue data,
 * mutates nothing external.
 * @param state The quote state.
 * @param grid The injected volume rate table (tiers + target margins).
 * @param opts The compute options (injected catalogue, purchase grids, margins, thresholds…).
 * @returns The priced quote.
 */
export function compute(state: QuoteState, grid: VolumeGrid, opts: ComputeOptions = {}): QuoteResult {
  const compatCheck = opts.compatCheck ?? false;
  // The catalogue is injected like the rate table: the engine receives prices, it does not decide where
  // they come from. Default = embedded (safety net, and a baseline identical to the cent).
  const cat = opts.catalogue ?? ACTIVE_CATALOGUE;
  // Injected purchase rate tables, same logic as the catalogue. Default = embedded.
  const paGrids = opts.paGrids ?? EMBEDDED_PA_GRIDS;
  const sourcePaMaterial = opts.sourcePaMaterial ?? MATERIAL_PA_SOURCE; // injected source, else the one bound at load; absent = embedded reference data
  const margins = opts.margins ?? engineSettings().technicalMargins; // injected technical margins, else the ones bound from app_settings (no embedded default)
  const supplyRef = opts.materialSupplyRef; // supply (distributor/brand) — UPZ blockers only, never the purchase price
  // The rate table (tiers + target margins) is injected, read from Supabase by the caller. Absent or
  // empty = explicit error (never a silent default).
  assertEngineDataBound();
  assertVolumeGrid(grid);
  const lines = state.lines;
  // Agency discount: free input (agencyDiscount) takes precedence over the frozen client record (clientIdx).
  const clientRecord = CLIENTS[state.clientIdx ?? 0] ?? { name: "", discount: 0 };
  const agencyDiscountRate = state.agencyDiscount != null ? state.agencyDiscount : clientRecord.discount;
  // The client's "top-of-table special discount" (mode B) is dropped. The agency rate alone drives the
  // 1st discount; a rate table's "specific discount" takes over per target. The purchase price only ever
  // sees the volume tier (invariant).
  const client = { name: clientRecord.name, discount: agencyDiscountRate };
  // The dedicated client rate table (§19) is normalised on input — the legacy shape (saved quote states,
  // current admin screen) migrates on the fly, to the cent. "A single active entry per target" surfaces
  // as a blocker at load time, never as a silent arbitration.
  const dedicatedGrid = normalizeClientRateCards(state.clientGrids);
  const rateCardError = validateEntries(dedicatedGrid);
  const forcedMaterials = state.forcedMaterials ?? {};
  const includeMinima = state.includeMinima ?? false;

  const res: QuoteResult = {
    lines: [],
    materials: {},
    flatFeeLines: [],
    totalPrint: 0,
    totalPvPublic: 0,
    totalBeforeDiscount: 0,
    generalDiscountAmountVal: 0,
    totalFinal: 0,
    totalTargetPa: 0,
    marginCoverage: { realLineCount: 0, printLineCount: 0, actualNetPv: 0, actualPa: 0, weightedTargetMarginFrac: null, paTotal: 0, breakdown: { negotiated: { n: 0, pa: 0 }, actual: { n: 0, pa: 0 }, target: { n: 0, pa: 0 } } },
    rush: { amount: 0, label: "", pa: 0 },
    blockers: [],
    serviceItem: { lines: [], servicesTotal: 0, servicesPaTotal: 0, alerts: [] },
    ancillaryCosts: { lines: [], total: 0, blockers: [] },
    fileCount: 0,
    elementCount: 0,
    totalM2: 0,
    totalWeight: 0,
  };

  // Cumulations (retroactive over the whole quote).
  const cumulatedMat = cumulateByCategory(lines, cat); // area per category (m² tiers), injected catalogue
  // Cumulated base: the volume already acquired by the contractual reference enters the tier cumulation
  // (sale and purchase read this same map). Injected, never read elsewhere.
  for (const [cat, off] of Object.entries(opts.referenceOffsets ?? {})) {
    if (off.m2 > 0) cumulatedMat.set(cat, (cumulatedMat.get(cat) ?? 0) + off.m2);
  }
  const cumulatedByFamily = cumulateByFamily(lines); // quantity per stand family
  const cumulatedAccessoryMl = new Map<string, number>(); // linear metres per standalone accessory (linked lines excluded)
  let cumulatedServiceHours = 0; // cumulated hourly-cutting hours
  for (const l of lines) {
    if (isAccessoryMlRegime(l) && l.ref && l.linkId == null && !l.isFinishingMl) {
      const linearMeters = num(l.L) > 0 ? num(l.L) * (num(l.fileQty) || 1) : num(l.fileQty) * num(l.copiesPerFile);
      cumulatedAccessoryMl.set(l.ref, (cumulatedAccessoryMl.get(l.ref) ?? 0) + linearMeters);
    } else if (isServiceRegime(l) && l.svcRef === "SRV-1705") {
      cumulatedServiceHours += isFilled(l.copiesPerFile) && num(l.copiesPerFile) > 0 ? num(l.copiesPerFile) : num(isFilled(l.svcHours) ? l.svcHours : 1);
    }
  }

  // Pass 1: per-line pricing.
  for (const l of lines) {
    const row: PricedLine = {
      id: l.id,
      type: l.type,
      m2: 0,
      quantity: num(l.fileQty) * num(l.copiesPerFile),
      pvM2: null,
      pu: null,
      theoreticalUnitPrice: 0,
      total: 0,
      pvCalc: null,
      matRef: null,
      combi: isLargeFormatPrintRegime(l) ? detectCombi(l) : "",
      forcedLine: false,
      forcedMat: false,
      floorApplied: false,
      flatFeeReplaced: false,
      targetPa: 0,
      targetPaM2: null,
      margin: 60,
      discountPct: 0,
    };
    const lineQuantity = row.quantity;

    // Material format: oversize (auto seam) & supply (pure test, injected margins).
    // The engine decides, the UI displays. Retained format = explicit choice (l.format) or the material's
    // ★ default. Unit dimensions of the piece, never the cumulation. Affects no price.
    if (isLargeFormatPrintRegime(l) && l.material) {
      const defaultFormat = DEFAULT_MAT_FORMAT[l.material];
      // Raw material (no printing IMP-1419 and no cutting CUT-1511): technical margins neutralised, as
      // there are no camera marks to preserve. Neutralisation is conditional on the line (book 4.8).
      const lineMargins = neutralizedMargins(l.print, l.cut) ? ZERO_MARGINS : margins;
      // The format picks itself, but only when nobody set it by hand. And it is derived, never written:
      // nothing is pushed into `l.format`, so clearing the field hands control back to the computation.
      // An unmapped material group is reported as a blocker (see buildBlockers); the format test is skipped for it.
      const nature = materialNatureOf(l.material) ?? "none";
      const auto = l.format
        ? null
        : formatAuto(MAT_FORMATS[l.material] ?? [], defaultFormat, num(l.L), num(l.H), nature, lineMargins);
      const formatRetained = (l.format && String(l.format)) || auto || defaultFormat || "";
      const verdict = checkOverflow(num(l.L), num(l.H), formatRetained, nature, lineMargins);
      const splice = effectiveSplice(l.spliced, verdict.overflows);
      if (formatRetained) row.formatRetained = formatRetained;
      // Gap to the ★ default, whatever its origin: this flag means "the retained format is not the
      // standard one", not "a human changed it". What matters to the workshop is that a web width must be
      // ordered, not who decided it — so here orange stops meaning "hand-edited" as it does elsewhere.
      row.formatNotDefault = !!(defaultFormat && formatRetained && formatRetained !== defaultFormat);
      row.formatAuto = !!auto; // who chose — the UI needs it for its tooltip, not for its colour
      row.formatTested = verdict.tested;
      row.exceedsFormat = verdict.overflows;
      row.spliced = splice;
      row.specificSupply = !!l.specificSupply;
      if (splice) row.clientNotice = SPLICE_NOTICE;
      // The two production notes are derived, never written into `l.prodNote`, which is free human input.
      // The production table displays them above it.
      const notes: string[] = [];
      if (row.formatNotDefault) notes.push(SUPPLY_NOTE);
      if (splice) notes.push(SPLICE_NOTE);
      if (notes.length) row.productionNotes = notes;
    }

    // Off-catalogue (free) line: free label, no catalogue reference → total = forced price × quantity,
    // purchase price mandatory, no material/printing/cutting validation.
    const isFreeform =
      !isCustomPrintRegime(l) &&
      !isUnitRegime(l) &&
      !isServiceRegime(l) &&
      !!(l.offCatalogue && String(l.offCatalogue).trim()) &&
      !((isLargeFormatPrintRegime(l) && l.material) || ((isAccessoryMlRegime(l) || isTierRegime(l)) && l.ref));

    // Completeness validation (non-free PRINT_GF: material + printing + cutting + L + H).
    if (isLargeFormatPrintRegime(l) && !isFreeform) {
      const missing: string[] = [];
      if (!l.material) missing.push("material");
      if (!l.print) missing.push("print");
      if (!l.cut) missing.push("cut");
      if (!(num(l.L) > 0)) missing.push("L");
      if (!(num(l.H) > 0)) missing.push("H");
      if (missing.length) row.missingFields = missing;
      // §17 gate compatibility check (opt-in): an item retained but forbidden for the material makes the
      // line incomplete — it is not priced, and never silently substituted. Off by default.
      if (compatCheck && l.material) {
        const incompatibleList: { field: string; ref: string }[] = [];
        /**
         * Records the item when it is forbidden for the line's material.
         * @param field The UI column of the item (also the catalogue gate field).
         * @param itemRef The retained item reference, if any.
         */
        const checkItem = (field: "print" | "cut" | "lamination" | "finishing1" | "finishing2", itemRef?: string): void => {
          if (itemRef && !itemAllowed(field, l.material, String(itemRef))) incompatibleList.push({ field, ref: String(itemRef) });
        };
        checkItem("print", l.print);
        checkItem("cut", l.cut);
        checkItem("lamination", l.lamination);
        checkItem("finishing1", l.finishing1);
        checkItem("finishing2", l.finishing2);
        if (incompatibleList.length) row.incompatibleFields = incompatibleList;
      }
    }

    if (isFreeform) {
      // Free line: forced price × quantity (m² for a print, linear metres for an accessory, unit otherwise).
      row.offCatalogue = true;
      row.name = String(l.offCatalogue).trim();
      const isM2Line = isLargeFormatPrintRegime(l);
      const isLinearMeter = isAccessoryMlRegime(l);
      const pieceM2 = unitM2(l);
      if (isM2Line) row.m2 = round3(pieceM2 * lineQuantity);
      const baseQty = isLinearMeter ? (num(l.L) > 0 ? num(l.L) * (num(l.fileQty) || 1) : lineQuantity) : lineQuantity;
      row.quantity = baseQty;
      const pu = num(l.forcedPv);
      row.pu = pu;
      if (isM2Line && pieceM2 > 0) row.pvM2 = pu / pieceM2;
      row.total = round2(pu * baseQty);
      row.forcedLine = true;
      // Purchase price mandatory, unless a real supplier purchase is entered for this free m² item.
      const purchase = isM2Line ? state.materialPurchases?.["LIBRE:" + String(l.offCatalogue).trim()] : undefined;
      const hasPurchase = !!purchase && num(purchase.pricePerM2) > 0;
      row.paMissing = !(isFilled(l.manualPaM2) || isFilled(l.manualPaU)) && !hasPurchase;
    } else if (isLargeFormatPrintRegime(l) && l.material && !row.incompatibleFields) {
      const materialEntry = findMat(l.material, cat);
      if (materialEntry) {
        row.matRef = l.material;
        const finishingMatRef = materialInFinishing(l, cat);
        const finishingMat = finishingMatRef ? findMat(finishingMatRef, cat) : undefined;
        const catKey = (materialEntry.category || l.material) + (finishingMat ? " + " + (finishingMat.category || finishingMatRef) : "");
        row._catKey = catKey;
        const pieceM2 = unitM2(l);
        const lineTotalM2 = pieceM2 * lineQuantity;
        row.m2 = round3(lineTotalM2);

        if (materialEntry.isMoq) {
          // Carpets: dedicated rate table + cutting, client discount on top, €/piece floor.
          row.isMoq = true;
          const cumulatedSurface = cumulatedMat.get(catKey) ?? lineTotalM2;
          const carpetPrice = carpetPriceM2(l, cumulatedSurface, cat);
          const moq = findMoq(l.material);
          // Carpet entry (§19, tufted only: MOQ-1201 goes through the standard regime, where an entry is
          // a plain material entry, propagation included).
          const moqEntry = moq ? resolveEntryRef(dedicatedGrid, "item", l.material) : undefined;
          let pvM2 = carpetPrice.basePvM2;
          row._fullPv = pvM2;
          if (isFilled(l.forcedPv)) {
            pvM2 = num(l.forcedPv);
            row.forcedLine = true;
          }
          // Client minimum unit price: substitutes the carpet's own floor (guard: the target carries one).
          // The floor stays applied in every regime — the unit floor is never lifted.
          const elementFloor = moqEntry?.clientMinUnitPrice != null && carpetPrice.minUnitM2 > 0 ? num(moqEntry.clientMinUnitPrice) : carpetPrice.minUnitM2;
          row.catalogueMinUnitPrice = round2(elementFloor); // carpet: floor already automatic (no override applied)
          if (elementFloor > 0 && !isFilled(l.forcedPv)) row._floorTotal = round2(elementFloor * lineQuantity);
          let retainedUnitPrice = isFilled(l.forcedPv) ? pieceM2 * pvM2 : Math.max(pieceM2 * pvM2, elementFloor);
          row._basePv = pvM2;
          // Carpet: dedicated rate table, no standard volume tier. The agency discount applies, but the
          // purchase price never includes it (_paDiscount = 0, book 4.5 §4).
          row.agencyDisc = client.discount;
          row.pvNetAg = round2(pvM2 * (1 - client.discount));
          row.tierRate = 0;
          row._paDiscount = 0;
          // Cascade: carpet = dedicated rate table, agency only, no volume tier → net tier PV = net agency PV.
          row.pvPublic = carpetPrice.basePvM2;
          row.tierNetPv = carpetPrice.basePvM2 * (1 - client.discount);
          row._targetMarginTierFrac = targetMarginForTier(cumulatedSurface, grid); // target margin of the volume
          if (moqEntry && moq && !row.forcedLine) {
            // Entry on a tufted carpet: native tiers of its own table (thresholds overridable by the
            // scale), % against the native price of the tier's lower bound, € taken as-is — and the
            // complex cutting extra stays full on top, never discounted. The entry substitutes the
            // client discount (no cumulation). The purchase price stays read at the native tier.
            const cutSurcharge = moqCutSurcharge(l.cut);
            const nativeThresholds = [0, ...MOQ_TIERS.slice(0, -1)]; // native upper bounds → lower bounds
            const thresholds = moqEntry.scale?.length
              ? entryThresholds(moqEntry, dedicatedGrid, grid)
              : nativeThresholds;
            const tierIdx = tierIndex(thresholds, cumulatedSurface);
            const minThreshold = thresholds[tierIdx]!;
            const nativeBoundPrice = moqPriceM2(moq, minThreshold); // the native price at the lower bound (convention)
            const tier = moqEntry.scale?.find((t) => t.minThreshold === minThreshold);
            const baseRate = effectiveBase(moqEntry, 0, nativeBoundPrice); // substitution: never the agency as inherited base
            let priceTier: number;
            let displayedRate: number;
            if (tier?.price != null) {
              priceTier = num(tier.price); // all-in client price, as-is
              displayedRate = nativeBoundPrice > 0 ? 1 - priceTier / nativeBoundPrice : 0;
              row.clientGrid = { id: moqEntry.id, mode: "net_price", value: round2(priceTier + cutSurcharge) };
            } else {
              const r = composeRate(baseRate, tier?.rate != null ? num(tier.rate) : 0);
              priceTier = nativeBoundPrice * (1 - r); // tier not entered: native price discounted by the base alone
              displayedRate = r;
              row.agencyGrid = { id: moqEntry.id, rate: baseRate, targetType: "item", targetRef: l.material, ...(moqEntry.name ? { name: moqEntry.name } : {}) };
              row.tierGrid = { id: moqEntry.id, rate: tier?.rate != null ? num(tier.rate) : 0, targetType: "item", targetRef: l.material, ...(moqEntry.name ? { name: moqEntry.name } : {}) };
            }
            pvM2 = priceTier + cutSurcharge;
            row.agencyDisc = baseRate;
            row.pvNetAg = round2(nativeBoundPrice * (1 - baseRate) + cutSurcharge);
            row.tierNetPv = pvM2;
            row._totalDiscount = carpetPrice.basePvM2 > 0 ? Math.max(0, 1 - pvM2 / carpetPrice.basePvM2) : 0;
            row.gridBreakdown = [{ role: "carpet", code: l.material, rate: displayedRate, entryId: moqEntry.id }];
            if (carpetPrice.basePvM2 > 0) row.forcedLineDiscount = effectiveDiscount(pvM2, carpetPrice.basePvM2) ?? 0;
            retainedUnitPrice = Math.max(pieceM2 * pvM2, elementFloor);
          } else {
            const carpetClientDiscount = Math.min(0.6, client.discount || 0);
            if (carpetClientDiscount > 0 && !row.forcedLine) {
              row._totalDiscount = carpetClientDiscount;
              pvM2 = round2(pvM2 * (1 - carpetClientDiscount));
              retainedUnitPrice = Math.max(pieceM2 * pvM2, elementFloor);
            }
          }
          const theoreticalMoqUnitPrice = pieceM2 * pvM2;
          row.theoreticalUnitPrice = round2(theoreticalMoqUnitPrice);
          row.floorApplied = elementFloor > theoreticalMoqUnitPrice && !isFilled(l.forcedPv);
          row.pvM2 = pvM2;
          row.pu = round2(retainedUnitPrice);
          row.total = round2(retainedUnitPrice * lineQuantity);
          const materialAggregate = (res.materials[catKey] ??= {
            ref: catKey,
            name: materialEntry.category || materialEntry.name,
            m2: cumulatedSurface,
            total: 0,
            minFlatFee: carpetPrice.minFlatFee,
            materialFlatFee: carpetPrice.minFlatFee,
            componentFlatFees: [], // all-in carpet: no component with a setup flat fee
            stockUpz: false,
            minTriggered: false,
          });
          materialAggregate.total += row.total;
          // Client flat fee (§19): substitutes the carpet's own fee (same guard).
          if (moqEntry?.clientFlatFee != null && materialAggregate.materialFlatFee > 0) {
            const clientFlatFee = num(moqEntry.clientFlatFee);
            materialAggregate.minFlatFee = materialAggregate.minFlatFee - materialAggregate.materialFlatFee + clientFlatFee;
            materialAggregate.materialFlatFee = clientFlatFee;
          }
        } else {
          // Standard regime: sum of components → tier → discount → final unit price.
          const tierInfo = marginTier(cumulatedMat.get(catKey) ?? 0, grid);
          const base = pvComponents(l, cat);
          row._basePv = base;
          row._fullPv = base;
          // Purchase-price lock: _paDiscount / _targetMarginTierFrac / _tierRate stay on the general volume tier,
          // never a client rate table (_tierRate is also inherited by linked linear-metre lines). §19
          // tables override the PV, never the purchase price.
          row._paDiscount = tierInfo.discountRate;
          row._targetMarginTierFrac = tierInfo.targetMargin;
          row._tierRate = tierInfo.discountRate;
          row.pvCalc = base * (1 - tierInfo.discountRate);
          row.pvPublic = base;

          const forcedMat = forcedMaterials[l.material];
          // Dedicated client rate table (§19) — combination > dedicated item > material propagation
          // (printing only) > standard. Fallback rule: with no applicable entry the standard path is the
          // original computation, identically — the table never takes anything away. Purchase-price lock
          // unchanged: no entry touches _paDiscount / _targetMarginTierFrac / _tierRate.
          const lineCategoryVolume = cumulatedMat.get(catKey) ?? 0;
          // The combination refs the line satisfies: the detected one (strict match), plus any ref
          // targeted by an entry whose signature the line contains — a component added beyond the
          // signature does not forfeit the negotiated commitment.
          const satisfiedCombis: string[] = row.combi ? [row.combi] : [];
          for (const e of dedicatedGrid.entries) {
            if (!activeEntry(e) || e.targetType !== "combination") continue;
            for (const r of e.refs) {
              if (!satisfiedCombis.includes(r) && lineContainsCombination(l, r)) satisfiedCombis.push(r);
            }
          }
          const lineEntries = resolveLineEntries(
            dedicatedGrid,
            { material: l.material, print: l.print, cut: l.cut, lamination: l.lamination, finishings: [l.finishing1, l.finishing2, l.finishing3, l.finishing4, l.finishing5, l.finishing6] },
            satisfiedCombis,
          );
          const servedCombiRef = lineEntries?.combination
            ? lineEntries.combination.refs.find((r) => satisfiedCombis.includes(r)) ?? row.combi
            : row.combi;
          const tierContext = { grid: dedicatedGrid, generalGrid: grid, agencyDiscount: client.discount };
          // The entry carrying the line's client minima: combination, else material.
          const minimaEntry = lineEntries?.combination ?? lineEntries?.material;
          const clientMinUnitPrice = minimaEntry?.clientMinUnitPrice;
          // Standard display by default — overwritten below when an entry serves the line.
          const effectiveTotalDiscount = lineDiscount(tierInfo.discountRate, client.discount);
          row._totalDiscount = effectiveTotalDiscount;
          row.agencyDisc = client.discount;
          row.pvNetAg = round2(base * (1 - client.discount));
          row.tierRate = tierInfo.discountRate;
          row.tierNetPv = base * (1 - effectiveTotalDiscount);
          // Tier of the line-scoped entry: combination (full = the combination's PV, so a component added
          // beyond the signature is discounted at the same rate), or a migrated item net price
          // (`linePriceScope`: full = the line's full PV, historical scope preserved).
          let lineEntry: RateCardEntry | null = null;
          let lineTier: ResolvedTier | null = null;
          if (lineEntries?.combination) {
            lineEntry = lineEntries.combination;
            lineTier = resolveEntryTier(lineEntry, lineCategoryVolume, { ...tierContext, fullTarget: fullCombination(servedCombiRef, cat) });
          } else if (lineEntries?.material?.linePriceScope) {
            lineEntry = lineEntries.material;
            lineTier = resolveEntryTier(lineEntry, lineCategoryVolume, { ...tierContext, fullTarget: base });
          }

          // Forecast, always computed even under a forced PV — the cascade columns stay an engine
          // forecast, badges included; only a € tier overwrites them below, like the legacy net price.
          let componentsPv: number | null = null;
          if (lineTier && lineEntry && !lineTier.inEuros) {
            // Combination regime in %: the composed rate applies to the line's complete price.
            const servedRef = lineEntries?.combination ? servedCombiRef : l.material;
            row._totalDiscount = lineTier.rate;
            row.agencyDisc = lineTier.base;
            row.tierRate = lineTier.scaleRate;
            row.pvNetAg = round2(base * (1 - lineTier.base));
            row.tierNetPv = base * (1 - lineTier.rate);
            row.agencyGrid = { id: lineEntry.id, rate: lineTier.base, targetType: lineEntries?.combination ? "combination" : "item", targetRef: servedRef, ...(lineEntry.name ? { name: lineEntry.name } : {}) };
            row.tierGrid = { id: lineEntry.id, rate: lineTier.scaleRate, targetType: lineEntries?.combination ? "combination" : "item", targetRef: servedRef, ...(lineEntry.name ? { name: lineEntry.name } : {}) };
            row.gridBreakdown = [{ role: lineEntries?.combination ? "combination" : "line", code: servedRef, rate: lineTier.rate, entryId: lineEntry.id }];
          } else if (lineEntries && !lineTier) {
            // Per-component resolution: each component of the full PV is discounted at its own rate —
            // material at its entry, printing at its own else propagation of the material entry's rate,
            // cutting/lamination/finishings at theirs else standard (material propagation never reaches
            // them). Exact sum, display rounding only.
            const components = splitPv(l, cat);
            const bareMaterialPv = components.find((c) => c.role === "material")?.pv ?? 0;
            const matTier = lineEntries.material
              ? resolveEntryTier(lineEntries.material, lineCategoryVolume, { ...tierContext, fullTarget: bareMaterialPv })
              : null;
            const componentBreakdown: NonNullable<PricedLine["gridBreakdown"]> = [];
            let componentsSum = 0;
            for (const cp of components) {
              let componentEntry: RateCardEntry | undefined;
              if (cp.role === "material") componentEntry = lineEntries.material;
              else if (cp.role === "print") componentEntry = lineEntries.print;
              else if (cp.role === "cut") componentEntry = lineEntries.cut;
              else if (cp.role === "lamination") componentEntry = lineEntries.lamination;
              else componentEntry = lineEntries.finishings[(cp.finishingIndex ?? 1) - 1];
              let rate: number;
              let entryId = "";
              if (cp.role === "material" && matTier && lineEntries.material) {
                rate = matTier.rate;
                entryId = lineEntries.material.id;
              } else if (cp.role === "print" && !componentEntry && matTier && lineEntries.material) {
                rate = matTier.rate; // material → printing propagation (that one only)
                entryId = lineEntries.material.id;
              } else if (componentEntry) {
                const componentTier = resolveEntryTier(componentEntry, lineCategoryVolume, { ...tierContext, fullTarget: cp.pv });
                rate = componentTier.rate;
                entryId = componentEntry.id;
              } else {
                rate = effectiveTotalDiscount; // component at the standard rate (agency ⊗ general tier)
              }
              componentsSum += cp.pv * (1 - rate);
              componentBreakdown.push({ role: cp.role, code: cp.code, rate, entryId: entryId });
            }
            componentsPv = componentsSum;
            row.gridBreakdown = componentBreakdown;
            row.tierNetPv = componentsSum;
            // The agency/tier discount columns describe the material entry (partial material + printing
            // base, detailed in the tooltip); without it they stay standard and only the blended rate
            // tells the rate-table story.
            if (matTier && lineEntries.material) {
              row.agencyDisc = matTier.base;
              row.tierRate = matTier.scaleRate;
              row.pvNetAg = round2(base * (1 - matTier.base));
              row.agencyGrid = { id: lineEntries.material.id, rate: matTier.base, targetType: "item", targetRef: l.material, ...(lineEntries.material.name ? { name: lineEntries.material.name } : {}) };
              row.tierGrid = { id: lineEntries.material.id, rate: matTier.scaleRate, targetType: "item", targetRef: l.material, ...(lineEntries.material.name ? { name: lineEntries.material.name } : {}) };
            }
            if (base > 0) {
              row.forcedLineDiscount = effectiveDiscount(componentsSum, base) ?? 0;
              row._totalDiscount = row.forcedLineDiscount;
            }
          }

          let pvM2: number;
          let skipFloor = false; // floors not applied: hand-forced price OR € tier (price as-is)
          if (isFilled(l.forcedPv)) {
            pvM2 = num(l.forcedPv);
            row.forcedLine = true;
            skipFloor = true;
          } else if (lineTier && lineTier.inEuros && lineEntry) {
            // Line-scoped € tier: the client price is taken as-is — the cascade is ignored and the floors
            // do not raise the price (the below-minimum alert stays, §21). The displayed % is the
            // reverse computation against the full price.
            pvM2 = base * (1 - lineTier.rate);
            row.clientGrid = { id: lineEntry.id, mode: "net_price", value: round2(pvM2) };
            skipFloor = true;
            row.tierRate = 0;
            row.agencyDisc = 0;
            row.pvNetAg = null;
            row.tierNetPv = null;
            if (base > 0) {
              row.forcedLineDiscount = effectiveDiscount(pvM2, base) ?? 0;
              row._totalDiscount = row.forcedLineDiscount;
            }
            row.gridBreakdown = [{ role: lineEntries?.combination ? "combination" : "line", code: lineEntries?.combination ? servedCombiRef : l.material, rate: lineTier.rate, entryId: lineEntry.id }];
          } else if (forcedMat && isFilled(forcedMat.pv)) {
            pvM2 = num(forcedMat.pv);
            row.forcedMat = true;
          } else if (lineTier && lineEntry) {
            pvM2 = base * (1 - lineTier.rate); // combination regime in % (forecast already displayed)
          } else if (componentsPv != null) {
            pvM2 = componentsPv; // per-component resolution (forecast already displayed)
          } else {
            pvM2 = base * (1 - effectiveTotalDiscount); // standard cascade — strictly the computation without a rate table
          }

          const lineFloor = elementFloor(l, forcedMat, cat, clientMinUnitPrice);
          const catalogueFloorVal = catalogueFloor(l, forcedMat, cat, clientMinUnitPrice);
          const theoreticalUnitPrice = pieceM2 * pvM2;
          const { pu, floorApplied } = defaultUnitPrice(theoreticalUnitPrice, lineFloor, skipFloor);
          row.catalogueMinUnitPrice = round2(catalogueFloorVal); // auto minimum unit price: line override excluded
          row.theoreticalUnitPrice = round2(theoreticalUnitPrice);
          row.floorApplied = floorApplied;
          // Net price strictly below the catalogue minimum unit price: floor not applied, visual alert only.
          if (row.clientGrid?.mode === "net_price" && catalogueFloorVal > 0 && theoreticalUnitPrice < catalogueFloorVal) row.belowMinUnitPrice = true;
          if (lineFloor > 0 && !skipFloor) row._floorTotal = round2(lineFloor * lineQuantity);
          row.pvM2 = pvM2;
          row.pu = round2(pu);
          row.total = round2(pu * lineQuantity);

          const materialAggregate = (res.materials[catKey] ??= {
            ref: catKey,
            name: (materialEntry.category || materialEntry.name) + (finishingMat ? " + " + (finishingMat.category || finishingMat.name) : ""),
            m2: cumulatedMat.get(catKey) ?? lineTotalM2,
            total: 0,
            minFlatFee: materialFlatFee(l, cat),
            materialFlatFee: materialFlatFee(l, cat),
            componentFlatFees: [],
            stockUpz: false,
            minTriggered: false,
          });
          materialAggregate.total += row.total;
          if (materialEntry.stockUpz) materialAggregate.stockUpz = true;
          // Client flat fee (§19): it substitutes the material fee in the consolidation, and only applies
          // when the target carries one in the catalogue (material fee > 0). Component fees always add up.
          if (minimaEntry?.clientFlatFee != null && materialAggregate.materialFlatFee > 0) {
            const clientFlatFee = num(minimaEntry.clientFlatFee);
            materialAggregate.minFlatFee = materialAggregate.minFlatFee - materialAggregate.materialFlatFee + clientFlatFee;
            materialAggregate.materialFlatFee = clientFlatFee;
          }
          // R-DP-1: component minimum flat fee, counted once per material and per distinct component
          // (deduped on the code across every line of that material).
          for (const componentFee of componentFlatFees(l, cat)) {
            if (!materialAggregate.componentFlatFees.some((x) => x.code === componentFee.code)) {
              materialAggregate.componentFlatFees.push({ code: componentFee.code, fee: componentFee.fee });
              materialAggregate.minFlatFee += componentFee.fee;
            }
          }
        }
      }
    } else if (isAccessoryMlRegime(l) && l.ref) {
      // Linear-metre accessory. Standalone: cumulated ml tier × client discount. Linked: follows its parent.
      const accessory = findAccessory(l.ref);
      if (accessory) {
        const linearMeters =
          l.linkId != null && l.autoDim
            ? autoMeterage(lines.find((x) => x.id === l.linkId) ?? l, l.autoDim)
            : num(l.L) > 0
              ? num(l.L) * (num(l.fileQty) || 1)
              : num(l.fileQty) * num(l.copiesPerFile);
        row.quantity = linearMeters;
        let pu = accessory.pu;
        if (l.linkId == null && !l.isFinishingMl) {
          const tierInfo = marginTier(cumulatedAccessoryMl.get(l.ref) ?? linearMeters, grid);
          const totalDiscount = lineDiscount(tierInfo.discountRate, client.discount);
          row._basePvU = accessory.pu;
          row._totalDiscount = totalDiscount;
          // Purchase-price lock: _tierRate / _paDiscount / _targetMarginTierFrac stay on the general ml tier — a
          // rate-table entry never touches them (and linked lines inherit _tierRate).
          row._tierRate = tierInfo.discountRate;
          row._paDiscount = tierInfo.discountRate;
          row._targetMarginTierFrac = tierInfo.targetMargin; // target margin of the ml tier itself
          row.agencyDisc = client.discount;
          row.pvNetAg = round2(accessory.pu * (1 - client.discount));
          row.tierRate = tierInfo.discountRate;
          pu = accessory.pu * (1 - totalDiscount);
          // Standalone ml accessory entry (§19): the entry thresholds are read in linear metres against
          // the ref cumulation; % composed, € as-is. A linked ml line never goes through here — it
          // follows its parent in the standard regime.
          const accEntry = resolveEntryRef(dedicatedGrid, "item", l.ref);
          if (accEntry) {
            const accessoryTier = resolveEntryTier(accEntry, cumulatedAccessoryMl.get(l.ref) ?? linearMeters, {
              grid: dedicatedGrid,
              generalGrid: grid,
              agencyDiscount: client.discount,
              fullTarget: accessory.pu,
            });
            pu = accessory.pu * (1 - accessoryTier.rate);
            row._totalDiscount = accessoryTier.rate;
            row.agencyDisc = accessoryTier.base;
            row.tierRate = accessoryTier.scaleRate;
            row.pvNetAg = round2(accessory.pu * (1 - accessoryTier.base));
            if (accessoryTier.inEuros) {
              row.clientGrid = { id: accEntry.id, mode: "net_price", value: round2(pu) };
            } else {
              row.agencyGrid = { id: accEntry.id, rate: accessoryTier.base, targetType: "item", targetRef: l.ref, ...(accEntry.name ? { name: accEntry.name } : {}) };
              row.tierGrid = { id: accEntry.id, rate: accessoryTier.scaleRate, targetType: "item", targetRef: l.ref, ...(accEntry.name ? { name: accEntry.name } : {}) };
            }
            row.gridBreakdown = [{ role: "accessory", code: l.ref, rate: accessoryTier.rate, entryId: accEntry.id }];
          }
        }
        row._fullPv = accessory.pu;
        if (isFilled(l.forcedPv)) {
          pu = num(l.forcedPv);
          row.forcedLine = true;
        }
        row.pu = round2(pu);
        row.total = round2(pu * linearMeters);
      }
    } else if (isCustomSizeSurcharge(l) && l.customSizeFinal) {
      // Custom-size extra (§9). It has neither its own reference nor its own quantity: everything
      // comes from its parent, whose row is already priced (it always precedes it).
      //
      // The inherited rate is not read from `_tierRate`, which is not filled on a stand line; it is derived
      // from the retained price against the catalogue tier-1 price (`inheritedRate`). `_basePvU` is used when
      // present because it is the price before the client discount, i.e. the tier alone — taking `pu`
      // would let the agency discount count twice.
      const parentLine = lines.find((m) => m.id === l.linkId);
      const parentRow = res.lines.find((x) => x.id === l.linkId);
      const parentStand = parentLine ? findStand(String(parentLine.ref ?? "")) : undefined;
      const customSizeRate = parentStand ? customSizeRateForStand(parentStand) : null;
      if (parentLine && parentStand && customSizeRate && parentStand.L > 0 && parentStand.H > 0) {
        const retainedTierPv = num(parentRow?._basePvU ?? parentRow?.pu ?? 0);
        const inheritedRatio = inheritedRate(retainedTierPv, num(parentStand.pv_tier1), !!parentRow?.forcedLine);
        const parentQuantity = num(parentLine.fileQty) * num(parentLine.copiesPerFile) || 1;
        const customSizePricing = priceCustomSize({
          base: { L: parentStand.L, H: parentStand.H }, final: l.customSizeFinal, rate: customSizeRate,
          parentQuantity: parentQuantity, inheritedTierRate: inheritedRatio, agencyDiscount: 0, // a stand takes no agency discount
        });
        row.quantity = parentQuantity;            // follows the parent: pieces, never m²
        row.pu = customSizePricing.pvUnit;         // € per piece → `total` enters totalPrint
        row.total = customSizePricing.pvTotal;
        row._paStand = customSizePricing.paUnit;   // purchase price per piece, used as-is by the margin block
        row.m2 = 0;                     // §9: does not feed the print m² counter
        row.customSize = {
          actualDeltaM2: customSizePricing.actualDeltaM2, billedDeltaM2: customSizePricing.billedDeltaM2,
          minSurfaceApplied: customSizePricing.minSurfaceApplied, appliedFloorPrice: customSizePricing.appliedFloorPrice,
          rateRef: customSizeRate.ref, ratePvM2: customSizeRate.pv_m2, base: { L: parentStand.L, H: parentStand.H },
        };
      }
    } else if (isTierRegime(l) && l.ref) {
      // Per-family tier regime (ref prefix): STAND (finished products included) or TRANSFER (TRS).
      // TRANSFER carries its own thresholds (6/11) and its rigid purchase price; STAND keeps 5/10 + slope.
      const stand = findStand(l.ref);
      if (stand) {
        const cumulatedQuantity = cumulatedByFamily.get(standFamily(l.ref)) ?? lineQuantity;
        // Stand / finished-product entry (§19 — never TRANSFER, its own out-of-scope regime; never a
        // modified composition, whose price comes from the sub-products): native quantity tiers
        // (0/5/10, thresholds overridable by the scale), % against the native lower-bound price, € as-is.
        // The entry substitutes the client discount, and the purchase price stays on the native tier.
        const standEntry = !isTransferRegime(l) && !isCompositionModified(l) ? resolveEntryRef(dedicatedGrid, "item", l.ref) : undefined;
        let standPriceResult: StandPrice;
        if (standEntry && !isFilled(l.forcedPv)) {
          const nativeThresholds = [0, 5, 10];
          const thresholds = standEntry.scale?.length ? entryThresholds(standEntry, dedicatedGrid, grid) : nativeThresholds;
          const tierIdx = tierIndex(thresholds, cumulatedQuantity);
          const minThreshold = thresholds[tierIdx]!;
          const nativeBoundPrice = tierUnitPrice(stand, minThreshold); // the native price at the lower bound (convention)
          const tier = standEntry.scale?.find((t) => t.minThreshold === minThreshold);
          const baseRate = effectiveBase(standEntry, 0, nativeBoundPrice); // substitution: never the inherited agency rate
          let pu: number;
          let displayedRate: number;
          if (tier?.price != null) {
            pu = num(tier.price); // client unit price of the tier, as-is
            displayedRate = nativeBoundPrice > 0 ? 1 - pu / nativeBoundPrice : 0;
            row.clientGrid = { id: standEntry.id, mode: "net_price", value: round2(pu) };
          } else {
            const r = composeRate(baseRate, tier?.rate != null ? num(tier.rate) : 0);
            pu = nativeBoundPrice * (1 - r); // tier not entered: native price discounted by the base alone
            displayedRate = r;
            row.agencyGrid = { id: standEntry.id, rate: baseRate, targetType: "item", targetRef: l.ref, ...(standEntry.name ? { name: standEntry.name } : {}) };
            row.tierGrid = { id: standEntry.id, rate: tier?.rate != null ? num(tier.rate) : 0, targetType: "item", targetRef: l.ref, ...(standEntry.name ? { name: standEntry.name } : {}) };
          }
          standPriceResult = { pu, pa: tierPa(stand, cumulatedQuantity), totalDiscount: Math.max(0, displayedRate), regime: "pack" };
          row._basePvU = tierUnitPrice(stand, cumulatedQuantity); // the native tier of the real cumulation (custom-size inheritance)
          row.gridBreakdown = [{ role: "stand", code: l.ref, rate: displayedRate, entryId: standEntry.id }];
        } else {
          // Stands never take the agency discount (their own quantity tiers apply); transfers still do.
          standPriceResult = isTransferRegime(l) ? transferPrice(l, stand, cumulatedQuantity, client.discount) : standPrice(l, stand, cumulatedQuantity, 0);
          if (standPriceResult.totalDiscount > 0) row._basePvU = standPriceResult.pu / (1 - standPriceResult.totalDiscount);
        }
        row._totalDiscount = standPriceResult.totalDiscount;
        row.pu = round2(standPriceResult.pu);
        row.total = round2(standPriceResult.pu * lineQuantity);
        // Forced PV: forced-line badge (the discount is already neutralised in standPrice/transferPrice).
        // §8 harmonises this badge on both tier regimes — presentation only, no pricing impact.
        if (isFilled(l.forcedPv)) row.forcedLine = true;
        row._paStand = standPriceResult.pa;
        row.standRegime = standPriceResult.regime; // pack (bundle) or composed (sum of sub-products) — badge + export
        row.m2 = standArea(stand, lineQuantity);
      }
    } else if (isUnitRegime(l)) {
      // Unit regime (SUR_UNIT / MENUIS_FAB): forced unit price × quantity (outside the print counters).
      // A negotiated purchase price is mandatory (§4/§6): missing → paMissing → red border + validation blocker.
      const pu = num(l.forcedPv);
      if (isFilled(l.forcedPv)) row.forcedLine = true;
      row.pu = pu;
      row.total = round2(pu * lineQuantity);
      row.paMissing = !(isFilled(l.manualPaU) || isFilled(l.marginManual));
    } else if (isCustomPrintRegime(l)) {
      // Custom-size print: total excl. VAT entered by hand, purchase price/margin mandatory.
      row.customPrint = true;
      const pieceM2 = unitM2(l);
      row.m2 = round3(pieceM2 * lineQuantity);
      const lineTotal = num(l.enteredTotal);
      row.total = round2(lineTotal);
      row.pu = lineQuantity > 0 ? lineTotal / lineQuantity : 0;
      if (pieceM2 > 0 && lineQuantity > 0) row.pvM2 = lineTotal / (pieceM2 * lineQuantity);
      row.forcedLine = true;
      row.paMissing = !(isFilled(l.manualPaU) || isFilled(l.marginManual));
    } else if (isServiceRegime(l)) {
      // Service (hourly cutting): hours × sale rate, degressive on the quote cumulation.
      row.isSvc = true;
      const hours = isFilled(l.copiesPerFile) && num(l.copiesPerFile) > 0 ? num(l.copiesPerFile) : num(isFilled(l.svcHours) ? l.svcHours : 1);
      const isCutting = l.svcRef === "SRV-1705";
      const rate = isCutting ? svcCutPvRate(cumulatedServiceHours) : num(l.svcRate || 185);
      row._svcCumulH = isCutting ? cumulatedServiceHours : null;
      const lineTotal = isFilled(l.enteredTotal) ? num(l.enteredTotal) : hours * rate;
      row.quantity = hours;
      row.pu = rate;
      row.total = round2(lineTotal);
      row.forcedLine = isFilled(l.enteredTotal);
    }

    res.lines.push(row);
  }

  // Propagation of the parent's tier to the linked linear-metre accessories (finishing or bungee).
  // The linked line inherits its parent's tier rate, composed with the agency discount. The purchase
  // price stays anchored to the inherited tier alone (client-invariant) through _paDiscount.
  res.lines.forEach((row, i) => {
    const l = lines[i]!;
    if (l.linkId == null || !isAccessoryMlRegime(l)) return;
    const parentRow = res.lines.find((x) => x.id === l.linkId);
    if (!parentRow || !((parentRow._tierRate ?? 0) > 0)) return;
    const rate = parentRow._tierRate!; // inherited tier alone
    row._linkDiscount = rate;
    row._paDiscount = rate;
    row._linkTargetMargin = parentRow._targetMarginTierFrac ?? BASE_MARGIN; // inherited target margin
    row.tierRate = rate;
    row.agencyDisc = client.discount;
    row._basePvLink = row.pu ?? 0; // base before discount (for the purchase price)
    // §19: linked lines stay in the standard regime (inherited tier ⊗ client-record agency rate) even
    // when the parent is under a rate table — material propagation never reaches them. Only exception:
    // a dedicated entry on their own ref, in which case the PV follows the entry while the purchase
    // price stays anchored to the inherited tier.
    const linkedEntry = l.ref ? resolveEntryRef(dedicatedGrid, "item", l.ref) : undefined;
    if (linkedEntry) {
      const fullUnitPrice = row.pu ?? 0;
      const linkedTier = resolveEntryTier(linkedEntry, row.quantity ?? 0, {
        grid: dedicatedGrid,
        generalGrid: grid,
        agencyDiscount: client.discount,
        fullTarget: fullUnitPrice,
      });
      row.tierRate = linkedTier.scaleRate;
      row.agencyDisc = linkedTier.base;
      if (row.pu != null) row.pu = round2(row.pu * (1 - linkedTier.rate));
      if (row.pvM2 != null) row.pvM2 = round2(row.pvM2 * (1 - linkedTier.rate));
      row.total = round2(row.total * (1 - linkedTier.rate));
      if (linkedTier.inEuros) row.clientGrid = { id: linkedEntry.id, mode: "net_price", value: row.pu ?? 0 };
      else {
        row.agencyGrid = { id: linkedEntry.id, rate: linkedTier.base, targetType: "item", targetRef: l.ref!, ...(linkedEntry.name ? { name: linkedEntry.name } : {}) };
        row.tierGrid = { id: linkedEntry.id, rate: linkedTier.scaleRate, targetType: "item", targetRef: l.ref!, ...(linkedEntry.name ? { name: linkedEntry.name } : {}) };
      }
      return;
    }
    const inheritedDiscount = lineDiscount(rate, client.discount); // inherited tier composed with the agency discount
    if (row.pu != null) row.pu = round2(row.pu * (1 - inheritedDiscount));
    if (row.pvM2 != null) row.pvM2 = round2(row.pvM2 * (1 - inheritedDiscount));
    row.total = round2(row.total * (1 - inheritedDiscount));
  });

  // Cumulation of supplied material per workshop (book §18, volume determining the purchase tier).
  // UPZ case: the print+cutting purchase tier is determined on the cumulation of every supplied-material
  // line at that workshop, not on the material cumulation — the workshop invoices a service, so its
  // degressivity applies to the volume it produces, all materials together.
  // Note: we read the material (isUpz), not row.matPa, which is only set inside the following pass 2.
  const suppliedCumulByWorkshop = new Map<string, number>();
  res.lines.forEach((row, i) => {
    const l = lines[i]!;
    if (row.isMoq || !isUpz(l.material) || !(row.m2 > 0)) return;
    const workshopName = String(l.workshopResp ?? "");
    if (!workshopName) return;
    suppliedCumulByWorkshop.set(workshopName, (suppliedCumulByWorkshop.get(workshopName) ?? 0) + row.m2);
  });

  // Pass 2: target purchase price per line (before the flat-fee switch).
  // res.lines is built 1:1 in the order of state.lines, so we pair by index (robust without ids).
  res.lines.forEach((row, i) => {
    const l = lines[i]!;
    const manualPaM2 = manualPaM2Of(l, row.m2);
    const hasManualPaM2 = manualPaM2 != null;
    const hasManualPaU = isFilled(l.manualPaU);
    const hasManualMargin = isFilled(l.marginManual);
    let paTotal: number;
    if (row.isSvc) {
      // Hourly cutting: purchase cost = hours × degressive rate; manual inputs take precedence.
      if (hasManualMargin) paTotal = row.total * (1 - num(l.marginManual) / 100);
      else if (hasManualPaU) paTotal = num(l.manualPaU) * row.quantity;
      else if (l.svcRef === "SRV-1705") paTotal = row.quantity * svcCutPaRate(row._svcCumulH ?? row.quantity);
      else paTotal = row.total * (1 - BASE_MARGIN);
    } else if (row._basePvLink != null) {
      // Linked ml accessory: purchase price anchored to the inherited tier alone (base before discount ×
      // slope on the parent's tier), hence invariant to the agency discount — book 4.5 §4.
      paTotal = (hasManualPaU ? num(l.manualPaU) : paTargetM2Fn(row._basePvLink, row._linkDiscount ?? 0, row._linkTargetMargin ?? BASE_MARGIN)) * row.quantity;
    } else if (row.customPrint) {
      // Custom-size print: entered unit purchase price × qty, or derived from the entered margin, else 0.
      if (hasManualPaU) paTotal = num(l.manualPaU) * row.quantity;
      else if (hasManualMargin) paTotal = row.total * (1 - num(l.marginManual) / 100);
      else paTotal = 0;
    } else if (row.matRef != null) {
      // Standard print / carpet: target purchase price = full cost × slope, with a 5 % floor per element.
      // UPZ (book 4.6 §18): the workshop purchase price only covers print+cutting (base = full − material
      // PV); the net material purchase price (gross × (1+waste)) adds up separately, to avoid double counting.
      const materialPaInfo = row.isMoq ? null : materialPa(l, sourcePaMaterial);
      if (materialPaInfo) row.matPa = materialPaInfo;
      const materialPv = materialPaInfo?.isUpz ? num(findMat(l.material, cat)?.pv) : 0; // material share removed from the full price (UPZ)
      const baseWorkshop = Math.max(0, (row._basePv ?? 0) - materialPv);
      // UPZ: the workshop's SUPPLIED rate table carries the print+cutting purchase price alone (the material
      // comes from UPSIZE). When the workshop has one it is a real price and takes precedence over the
      // target, as everywhere else. Without a rate table we fall back to the target: never a hole.
      const upzRateTable =
        materialPaInfo?.isUpz && !hasManualPaM2
          ? paSupplierLookup(l, "SUPPLIED", {
              // Volume = everything this workshop prints on supplied material for this quote (book §18).
              cumulatedM2: suppliedCumulByWorkshop.get(String(l.workshopResp ?? "")) ?? row.m2,
              grid,
              paGrids,
            })
          : null;
      if (upzRateTable) {
        row._paWorkshopIsActual = true; // the workshop purchase price is negotiated, not targeted
        if (upzRateTable.min_flat_fee != null) row._paMinFlatFee = upzRateTable.min_flat_fee; // consolidated per group in Pass B
      }
      const paM2 = hasManualPaM2
        ? manualPaM2!
        : upzRateTable
          ? upzRateTable.pa
          : paTargetM2Fn(baseWorkshop, row._paDiscount ?? 0, row._targetMarginTierFrac ?? BASE_MARGIN);
      const pieceM2 = unitM2(l);
      const workshopPaTotal = targetPaTotal({ paM2, m2unit: pieceM2, qty: row.quantity, m2total: row.m2, manual: hasManualPaM2 });
      row._workshopPaTot = round2(workshopPaTotal);
      const materialPaTotal = materialPaInfo?.isUpz && materialPaInfo.net != null ? round2(materialPaInfo.net * row.m2) : 0; // net material spread per m²
      if (materialPaInfo?.isUpz && materialPaInfo.grossMissing) row.paMatMissing = true; // gross missing → soft signal (real margin null)
      paTotal = round2(workshopPaTotal + materialPaTotal);
      // Pure target: the regime's target rate, independent of any negotiated/rate-table value, plus the
      // net UPZ material. Feeds the "target" column of the 3 groups (targetPaM2 is the retained price).
      row.pureTargetPaM2 = round2(paTargetM2Fn(baseWorkshop, row._paDiscount ?? 0, row._targetMarginTierFrac ?? BASE_MARGIN) + (materialPaInfo?.isUpz && materialPaInfo.net != null ? materialPaInfo.net : 0));
    } else if (row._paStand != null) {
      // Stand / transfer: tier purchase price, or negotiated — entered unit price, or derived from the
      // entered margin % (reciprocal coupling, §6). The negotiated one wins over the tier price.
      if (hasManualMargin) paTotal = row.total * (1 - num(l.marginManual) / 100);
      else paTotal = (hasManualPaU ? num(l.manualPaU) : row._paStand) * row.quantity;
    } else {
      // Unit regime (SUR_UNIT / MENUIS_FAB) and standalone ml accessory: negotiated purchase price —
      // entered per unit, or derived from the entered margin % (§6) — else the default standard slope.
      let paU: number;
      if (hasManualMargin) paU = (row.pu ?? 0) * (1 - num(l.marginManual) / 100);
      else if (hasManualPaU) paU = num(l.manualPaU);
      else if (row._basePvU != null) paU = paTargetM2Fn(row._basePvU, row._paDiscount ?? 0, row._targetMarginTierFrac ?? BASE_MARGIN);
      else paU = row.pu != null ? row.pu * (1 - BASE_MARGIN) : 0;
      paTotal = paU * row.quantity;
    }
    // Negotiated regime outside print: on other regimes the human decision goes through the unit purchase
    // price or the margin % (§6), so without this flag a hand-decided price on a stand would be reported
    // as "target" on the banner and the production table.
    if (!isLargeFormatPrintRegime(row)) row.paNegotiated = hasManualPaU || hasManualMargin || hasManualPaM2;
    row._paGross = round2(paTotal);
  });

  // Minimum flat fee consolidation.
  for (const aggregate of Object.values(res.materials)) {
    // Cumulated trigger base: the turnover already acquired by the reference counts towards the trigger,
    // so a reference above the fee does not make it bite again on a small addition. The material's
    // displayed total stays the one of the computed lines.
    const alreadyAcquired = opts.referenceOffsets?.[aggregate.ref]?.euros ?? 0;
    if (!triggerFlatFee(aggregate.total + alreadyAcquired, aggregate.minFlatFee)) continue;
    let aggregatedCost = 0;
    for (const row of res.lines) {
      if (row._catKey === aggregate.ref) {
        aggregatedCost += row._paGross ?? 0;
        row.total = 0;
        row.flatFeeReplaced = true;
      }
    }
    res.flatFeeLines.push({
      ref: aggregate.ref,
      name: aggregate.name,
      amount: aggregate.minFlatFee,
      computedTotal: round2(aggregate.total),
      materialFlatFee: aggregate.materialFlatFee,
      componentFlatFees: aggregate.componentFlatFees,
      targetPa: flatFeePa(aggregate.minFlatFee, state.forcedMinFeePa?.[aggregate.ref]),
      actualCost: round2(aggregatedCost),
      // Filled below by `computeFlatFeePa`: a flat fee's purchase price needs the workshop minima,
      // which are only consolidated in Pass B, i.e. after this switch.
      paRetained: 0,
      paOrigin: "target",
    });
    aggregate.minTriggered = true;
  }

  // Effective discount (forced lines) & stale override.
  res.lines.forEach((row, i) => {
    const l = lines[i]!;
    if (row.forcedLine && isFilled(l.forcedPv) && (row._fullPv ?? 0) > 0) {
      row.forcedLineDiscount = effectiveDiscount(row.pvM2 ?? num(l.forcedPv), row._fullPv!);
    }
    if (isLargeFormatPrintRegime(l) && isFilled(l.forcedPv) && l.forcedPvSnapshot != null) {
      row.forceStale = l.forcedPvSnapshot !== compositionSnapshot(l);
    }
  });

  // Displayed purchase price & per-line margin.
  let totalTargetPa = 0;
  for (const row of res.lines) {
    if (row.flatFeeReplaced || row.total === 0) {
      row.targetPa = 0;
      row.targetPaM2 = null;
      row.pureTargetPaM2 = null;
      row.margin = 0;
      continue;
    }
    const paTotal = row._paGross ?? 0;
    row.targetPa = round2(paTotal);
    row.margin = marginPct(row.total, paTotal);
    if (isAccessoryMlRegime(row)) row.targetPaM2 = row.quantity > 0 ? round2(paTotal / row.quantity) : null; // purchase price per ml
    else if (isTierRegime(row)) row.targetPaM2 = null; // driven by the unit purchase price
    else if (row.matPa?.isUpz)
      row.targetPaM2 = row.m2 > 0 && row._workshopPaTot != null ? round2(row._workshopPaTot / row.m2) : null; // UPZ: €/m² = workshop only
    else row.targetPaM2 = row.m2 > 0 ? round2(paTotal / row.m2) : null;
    totalTargetPa += row.targetPa;
  }
  for (const flatFeeLine of res.flatFeeLines) totalTargetPa += flatFeeLine.targetPa;
  res.totalTargetPa = round2(totalTargetPa);

  // Actual supplier purchase prices (subcontractor × mode, 1 m² P8 minimum).
  // Actual purchases come first: `computePaSupplier` (Pass B) compounds a UPZ line's purchase price by adding
  // the material to the workshop, so it must already know whether that material is purchased (invoiced
  // cost) or merely estimated (reference data). The order is not a convenience.
  applyMaterialPurchases(state, res, margins);
  computePaSupplier(state, res, grid, cumulatedMat, paGrids);

  // Counters (files, elements, m², weight).
  computeCounters(lines, res, cat);

  // Services.
  res.serviceItem = computeServices(state.services ?? {}, res, dedicatedGrid);

  // Extra purchase fees (book 4.9): margin impact only — computed before the coverage (the total enters
  // the displayed total purchase price), never in the sale totals nor the discount bases.
  res.ancillaryCosts = computeAncillaryCosts(state.ancillaryCosts);

  // Purchase price of the minimum flat fees — after Pass B (workshop minima consolidated).
  computeFlatFeePa(res);

  // Margin coverage: aggregated over the real-purchase-price lines only.
  computeMarginCoverage(res);

  // Print total & cascade.
  let totalPrint = 0;
  for (const row of res.lines) totalPrint += row.total;
  for (const flatFeeLine of res.flatFeeLines) totalPrint += flatFeeLine.amount;
  res.totalPrint = round2(totalPrint);

  const servicesTotal = res.serviceItem.servicesTotal;
  res.totalBeforeDiscount = round2(res.totalPrint + servicesTotal);

  // Base of the general discount: the total before, minus the lines taken out by hand
  // (`excludingGeneralDiscount`). Extra purchase fees are not in it — they are in no sale total at all. Lines
  // carried by a minimum flat fee stay out of the base as before (`flatFeeReplaced`), which
  // `generalDiscountAmount` knows about.
  /**
   * Whether a line is taken out of the general discount base by hand.
   * @param i The line index.
   * @returns True when the line is excluded.
   */
  const isExcluded = (i: number) => state.lines[i]?.excludingGeneralDiscount === true;
  const baseLines = res.lines
    .map((r, i) => ({ r, i }))
    .filter(({ i }) => !isExcluded(i))
    .map(({ r }) => ({
      total: r.total,
      ...(r._floorTotal != null ? { floorTotal: r._floorTotal } : {}),
      flatFeeReplaced: r.flatFeeReplaced,
    }));
  const excludedTotal = round2(res.lines.reduce((s, r, i) => (isExcluded(i) ? s + r.total : s), 0));
  const discountBase = round2(res.totalBeforeDiscount - excludedTotal);

  const discountInput = { totalBefore: discountBase, servicesTotal, lines: baseLines, includeMinima: includeMinima };
  // Maximum discount of the base (100 % rate): it carries the knowledge of the floors, and it is what
  // the target resolution receives to bound the descent.
  const maxDiscount = generalDiscountAmount({ ...discountInput, rate: 1 });

  const servicesBlock = state.services ?? {};
  const rushOpt = servicesBlock.rush ? findRushOpt(servicesBlock.rush) : undefined;
  const rushTarget: RushTarget | undefined = rushOpt?.code
    ? rushOpt.is_negotiated
      ? { negotiated: true, negotiatedAmount: num(servicesBlock.rushNego) }
      : { pct: rushOpt.pct ?? 0, floor: rushOpt.floor_amount ?? 0 }
    : undefined;

  const generalRate = generalDiscountRate(state.discountGen);
  const isTargetMode = state.discountGenMode === "target";
  if (isTargetMode) {
    res.generalDiscountTargetInfo = resolveDiscountTarget(state, res, {
      discountBase: discountBase, maxDiscount, rush: rushTarget, excludedCount: res.lines.filter((_, i) => isExcluded(i)).length,
    });
    res.generalDiscountAmountVal = res.generalDiscountTargetInfo.amountFrozen ?? 0;
  } else {
    res.generalDiscountAmountVal = generalDiscountAmount({ ...discountInput, rate: generalRate });
  }
  const discountedBase = round2(res.totalBeforeDiscount - res.generalDiscountAmountVal);

  res.rush = rush(rushOpt, discountedBase, servicesBlock.rushNego, servicesBlock.rushPa);
  res.totalFinal = round2(discountedBase + res.rush.amount);

  // The gap to the target is only known here, once the final total is settled — it feeds the permanent
  // banner and the "recalibrate on the target" button.
  if (res.generalDiscountTargetInfo) finalizeTargetGap(res.generalDiscountTargetInfo, res.totalFinal, state, res, isExcluded);

  // Total public PV (before discounts): full × m² per line (print/carpet), else net, plus services at
  // full price. Feeds the bottom banner (total discounts = public − final).
  let totalPvPublic = 0;
  for (const row of res.lines) {
    if (row.flatFeeReplaced) continue;
    totalPvPublic += row._fullPv != null && row.m2 > 0 ? row._fullPv * row.m2 : row.total;
  }
  res.totalPvPublic = round2(totalPvPublic + servicesTotal);

  // Effective discount per line (Rem. % column): composed tier + client (_totalDiscount), else the discount
  // inherited from the link (_linkDiscount), else 0. Forced-price lines also carry forcedLineDiscount (gap to full).
  for (const row of res.lines) row.discountPct = row._totalDiscount ?? row._linkDiscount ?? 0;

  // Blockers.
  evaluateUpzBlockers(state, res, supplyRef); // sets row.upzBlockers — only when the supply reference data is injected
  res.blockers = buildBlockers(state, res, opts.batch === true);
  if (rateCardError) res.blockers.push(rateCardError); // client rate table conflict (two identical modes/targets)
  res.blockers.push(...res.ancillaryCosts.blockers); // incomplete extra fee = validation refused
  applyMarginThresholds(res, opts?.marginThresholds); // quote margin: alert below the target, blocker below the floor

  return res;
}

/**
 * Looks up a rush option by its code.
 * @param code The rush option code.
 * @returns The matching option, or undefined.
 */
function findRushOpt(code: string) {
  return RUSH_OPTS.find((o) => o.code === code);
}

// `cat` is passed for uniformity — this function only reads weight (kg), which is identity data and
// therefore always embedded.
/**
 * Recap counters: files, elements, m², weight (book §10). Sets the counters on the result.
 * @param lines The quote lines.
 * @param res The quote result being built (counters are written on it).
 * @param cat The catalogue.
 */
function computeCounters(lines: readonly QuoteLine[], res: QuoteResult, cat: Catalogue): void {
  let fileCount = 0;
  let elementCount = 0;
  let totalM2 = 0;
  let totalWeight = 0;
  for (const l of lines) {
    const lineQuantity = num(l.fileQty) * num(l.copiesPerFile);
    if (isLargeFormatPrintRegime(l) && l.material) {
      const materialEntry = findMat(l.material, cat);
      fileCount += num(l.fileQty);
      elementCount += lineQuantity;
      const m2 = unitM2(l) * lineQuantity;
      totalM2 += m2;
      if (materialEntry) totalWeight += m2 * num(materialEntry.kg);
      const finishingMatRef = materialInFinishing(l, cat);
      const finishingMat = finishingMatRef ? findMat(finishingMatRef, cat) : undefined;
      if (finishingMat) totalWeight += m2 * num(finishingMat.kg);
    } else if (isCustomPrintRegime(l)) {
      fileCount += num(l.fileQty);
      elementCount += lineQuantity;
      const m2 = unitM2(l) * lineQuantity;
      totalM2 += m2;
      totalWeight += m2 * num(l.kgM2 ?? 0);
    } else if (isTierRegime(l)) {
      const stand = findStand(l.ref);
      fileCount += num(l.fileQty);
      elementCount += lineQuantity;
      if (stand) totalWeight += lineQuantity * num(stand.kg);
      // Printed fabric of the stand, at the final format when a custom-size extra carries one (the extra line itself
      // adds nothing: its area is already in its parent's). Transfers share the tier regime but are not stands.
      if (stand && l.type === "STAND" && !isCustomSizeSurcharge(l)) {
        totalM2 += standLinePrintedM2(l, stand, lineQuantity, extraOf(l, lines)?.customSizeFinal);
      }
    } else if (isAccessoryMlRegime(l) && l.ref) {
      const accessory = findAccessory(l.ref);
      const linearMeters = num(l.L) > 0 ? num(l.L) * (num(l.fileQty) || 1) : lineQuantity;
      if (accessory) totalWeight += linearMeters * num(accessory.kg ?? 0);
    }
  }
  res.fileCount = Math.round(fileCount);
  res.elementCount = Math.round(elementCount);
  res.totalM2 = round2(totalM2);
  res.totalWeight = round2(totalWeight);
}

// `grid` + `cumulatedMat`: purchase tiers are read on the same volume and the same thresholds as the sale
// tiers (a single truth of bounds). The cumulation is the material's category one (row._catKey), exactly
// like marginTier() above: at exactly 10 m², purchase and sale switch together.
/**
 * Actual supplier purchase prices: INCLUDED/SUPPLIED mode, atomic lookup, 1 m² Prodhuit minimum, real margin.
 * @param state The quote state.
 * @param res The quote result being built (priced lines are completed in place).
 * @param grid The injected volume rate table.
 * @param cumulatedMat Cumulated m² per material category.
 * @param paGrids The purchase rate tables.
 */
function computePaSupplier(state: QuoteState, res: QuoteResult, grid: VolumeGrid, cumulatedMat: Map<string, number>, paGrids: PaRateCards): void {
  const generalRate = generalDiscountRate(state.discountGen);
  // Pass A: services purchase price €/m², mode, net PV, purchase group (paired by index).
  // A purchase group = workshop | material | printing | cutting | lamination | finishing | mode. It is
  // the unit the workshop invoices, hence the unit of its minima (book §18): two different jobs = two
  // setups = two minima. The group accumulates its area (m² floor) and its workshop purchase price (€ floor).
  const groups = new Map<string, GroupCumulation>();
  /**
   * Adds a line's area and workshop purchase price to its purchase group.
   * @param groupKey The purchase group key.
   * @param m2 The area to add.
   * @param workshopPa The workshop purchase price to add.
   * @param minFlatFee The workshop flat fee of the group, if any.
   */
  const addToGroup = (groupKey: string, m2: number, workshopPa: number, minFlatFee?: number) => {
    const group = groups.get(groupKey) ?? { m2: 0, workshopPa: 0 };
    group.m2 += m2;
    group.workshopPa += workshopPa;
    if (minFlatFee != null) group.min_flat_fee = minFlatFee; // same key ⇒ same flat fee (the import forbids duplicates)
    groups.set(groupKey, group);
  };

  res.lines.forEach((row, i) => {
    if (!isLargeFormatPrintRegime(row) || row.matRef == null || !(row.m2 > 0)) return;
    const l = state.lines[i]!;
    const mode = paMode(l);
    const manualPaM2 = manualPaM2Of(l, row.m2);
    const hasManualPaM2 = manualPaM2 != null;
    row.paNegotiated = hasManualPaM2; // "negotiated" regime (hand-entered) — drives the banner breakdown
    const workshop = String(l.workshopResp ?? "");
    row._pvNet = round2((row.flatFeeReplaced ? 0 : row.total) * (1 - generalRate));
    row.lineNetPv = row._pvNet; // exposed for the margin → negotiated purchase price conversion in the UI

    // UPZ: the workshop purchase price (print+cutting) comes from Pass 2 (_workshopPaTot) — the material is
    // never in it. The group still exists: a workshop flat fee compares against the workshop price alone.
    if (row.matPa?.isUpz) {
      row.paMode = mode;
      row.servicePaM2 = row._workshopPaTot != null && row.m2 > 0 ? round2(row._workshopPaTot / row.m2) : null;
      if (workshop && row._workshopPaTot != null && !hasManualPaM2) {
        const groupKey = workshop + "|" + paGroupKeyP8(l, mode);
        addToGroup(groupKey, row.m2, row._workshopPaTot, row._paMinFlatFee);
        row._paGroup = groupKey;
        row._paWorkshop = workshop;
      }
      // Side-by-side regimes: a UPZ line's "rate table" is the workshop rate; negotiated = manual entry.
      row.gridPaM2 = row.servicePaM2;
      row.negotiatedPaM2 = manualPaM2;
      return;
    }

    const lookup = hasManualPaM2 ? null : paSupplierLookup(l, mode, { cumulatedM2: cumulatedMat.get(row._catKey ?? "") ?? row.m2, grid, paGrids });
    const servicePaM2 = hasManualPaM2 ? manualPaM2 : lookup ? lookup.pa : null;
    row.paMode = mode;
    row.servicePaM2 = servicePaM2;
    if (servicePaM2 != null && workshop) {
      const groupKey = workshop + "|" + paGroupKeyP8(l, mode);
      addToGroup(groupKey, row.m2, servicePaM2 * row.m2, lookup?.min_flat_fee);
      row._paGroup = groupKey;
      row._paWorkshop = workshop;
    }
    // Side-by-side regimes: the rate-table rate is always exposed, even when a negotiated one covers it
    // (the lookup is then short-circuited for the machinery, so we redo it read-only for display).
    const displayLookup = lookup ?? (hasManualPaM2 ? paSupplierLookup(l, mode, { cumulatedM2: cumulatedMat.get(row._catKey ?? "") ?? row.m2, grid, paGrids }) : null);
    row.gridPaM2 = displayLookup ? displayLookup.pa : null;
    row.negotiatedPaM2 = manualPaM2;
  });
  // Pass B: invoicing minima per purchase group + real margin.
  // The floor (m² at PRODHUIT, € at INS) is prorated across the group's lines: each keeps its share. The
  // retained purchase price stays a real one — displayed in black, with no new colour.
  /**
   * Billing effect of the workshop minimum on a line.
   * @param row The priced line.
   * @returns The factor to apply to the line's workshop purchase price and the type of minimum.
   */
  const minimumEffect = (row: PricedLine): ReturnType<typeof groupMinimum> => {
    // A negotiated purchase price is a human decision: never adjusted by a workshop minimum (book §18).
    // Without this guard the 1 m² floor would inflate a hand-entered price on a small piece.
    if (row.paNegotiated) return { factor: 1, type: null as "m2" | "euros" | null };
    const group = row._paGroup ? groups.get(row._paGroup) : undefined;
    const workshop = row._paWorkshop ?? "";
    return group && workshop ? groupMinimum(workshop, group) : { factor: 1, type: null as "m2" | "euros" | null };
  };
  /**
   * Flags the line when a workshop minimum applied.
   * @param row The priced line.
   * @param minimumInfo The minimum effect computed for the line.
   */
  const flagMinimum = (row: PricedLine, minimumInfo: ReturnType<typeof groupMinimum>): void => {
    if (minimumInfo.factor === 1) return;
    if (minimumInfo.type === "m2") row.paMini1m2 = true; // PRODHUIT: 1 m² per purchased combination
    else {
      row.paFlatFeeWorkshop = minimumInfo.value ?? null; // INS: the key's € flat fee bit (tooltip)
      row.paMinApplied = true;
    }
  };

  for (const row of res.lines) {
    // UPZ: real margin on the decomposed retained purchase price (workshop print+cutting + net material);
    // the displayed €/unit price stays the workshop one. Missing gross material → null margin.
    if (row.matPa?.isUpz) {
      const minimumInfo = minimumEffect(row);
      flagMinimum(row, minimumInfo);
      // SUPPLIED: the flat fee compares against the workshop price alone. The net material adds on top,
      // never absorbed by the fee: it comes from UPSIZE, the workshop does not invoice it.
      const workshopPaTotal = row._workshopPaTot != null ? round2(row._workshopPaTot * minimumInfo.factor) : null;
      if (workshopPaTotal != null) {
        row._workshopPaTot = workshopPaTotal;
        if (row.m2 > 0) row.servicePaM2 = round2(workshopPaTotal / row.m2);
      }
      row.paServiceTot = workshopPaTotal;
      // The material: actual purchase cost when entered, else the reference estimate. Never both — the
      // purchase replaces, it does not add up, otherwise the material would be counted twice.
      const materialPaTotal = row._purchaseShare != null
        ? row._purchaseShare
        : (row.matPa.net != null ? round2(row.matPa.net * row.m2) : 0);
      const paRetained = workshopPaTotal != null ? round2(workshopPaTotal + materialPaTotal) : (row._paGross ?? 0);
      row._paGross = paRetained; // the retained purchase price feeds the aggregates (banner, summary)
      const pvNet = row._pvNet ?? 0;
      // An entered purchase restores the real margin even without a reference price: the cost is no
      // longer estimated, it is invoiced.
      const isActual = (row._purchaseShare != null || !row.matPa.grossMissing) && pvNet > 0;
      row.actualMargin = isActual ? Math.round((1 - paRetained / pvNet) * 1000) / 10 : null;
      row.effectiveMarginM2 = isActual && row.m2 > 0 ? round2((pvNet - paRetained) / row.m2) : null;
      continue;
    }
    if (row.servicePaM2 == null || !(row.m2 > 0)) {
      row.paServiceTot = null;
      row.actualMargin = null;
      row.effectiveMarginM2 = null;
      continue;
    }
    const minimumInfo = minimumEffect(row);
    flagMinimum(row, minimumInfo);
    row.paServiceTot = servicePaTotal(row.servicePaM2, row.m2, minimumInfo.factor);
    const actualPaTotal = row.paServiceTot;
    const pvNet = row._pvNet ?? 0;
    row.actualMargin = pvNet > 0 ? Math.round((1 - actualPaTotal / pvNet) * 1000) / 10 : null;
    // Effective margin €/m² on the real purchase price (same gate as actualMargin) — never on the target.
    row.effectiveMarginM2 = pvNet > 0 && row.m2 > 0 ? round2((pvNet - actualPaTotal) / row.m2) : null;
  }
  // Pass C: target margin of the active tier (always filled) + target €/m² + orange alert (book 4.5 §5).
  // Non-blocking: a real margin below the tier target raises marginBelowTarget, never a blocker.
  for (const row of res.lines) {
    row.targetMarginTier = row._targetMarginTierFrac != null ? Math.round(row._targetMarginTierFrac * 1000) / 10 : null;
    // Target €/m² = net PV €/m² × tier target rate. A reference independent of the real cost.
    row.marginTargetM2 =
      row._targetMarginTierFrac != null && row.lineNetPv != null && row.m2 > 0
        ? round2((row.lineNetPv / row.m2) * row._targetMarginTierFrac)
        : null;
    // MENUIS_FAB regime (joinery, free lines): no tier, hence no targetMarginTier. Fixed 40 % target — a
    // margin below 40 % raises the orange alert, non-blocking (only a missing purchase price blocks here).
    row.marginBelowTarget = isJoineryFabRegime(row)
      ? !row.paMissing && row.margin != null && row.margin < 40
      : row.actualMargin != null && row.targetMarginTier != null && row.actualMargin < row.targetMarginTier;
  }
  // Pass D: purchase price broken down into the 3 side-by-side regimes — €/unit + margin % per regime.
  // Gross rates (never the workshop minimum). Display only.
  res.lines.forEach((row, rowIndex) => {
    if (!isLargeFormatPrintRegime(row)) return;
    const unitArea = row.quantity > 0 ? row.m2 / row.quantity : 0; // unit area (m²/piece)
    const pvNet = row.lineNetPv ?? 0;
    /**
     * Margin % of a purchase rate against the line net PV.
     * @param rate The purchase rate €/m².
     * @returns The margin %, or null when it cannot be computed.
     */
    const marginPctOf = (rate: number | null | undefined) =>
      rate != null && pvNet > 0 && row.m2 > 0 ? Math.round((1 - (rate * row.m2) / pvNet) * 1000) / 10 : null;
    row.targetPaU = row.pureTargetPaM2 != null ? round2(row.pureTargetPaM2 * unitArea) : null;
    row.gridPaU = row.gridPaM2 != null ? round2(row.gridPaM2 * unitArea) : null;
    row.gridMarginPct = marginPctOf(row.gridPaM2);
    row.negotiatedPaU = row.negotiatedPaM2 != null ? round2(row.negotiatedPaM2 * unitArea) : null;
    const typedTotal = state.lines[rowIndex]?.manualPaTotal;
    row.negotiatedPaTotal = isFilled(typedTotal) ? round2(num(typedTotal)) : row.negotiatedPaM2 != null ? round2(row.negotiatedPaM2 * row.m2) : null;
    row.negotiatedMarginPct = marginPctOf(row.negotiatedPaM2);
    if (isFilled(typedTotal)) {
      // A typed total fills the €/unit and the margin by itself, even before the line has a surface.
      const fromTotal = negotiatedFromTotal(num(typedTotal), row.quantity, pvNet, row.m2);
      row.negotiatedPaM2 = row.negotiatedPaM2 ?? fromTotal.paM2;
      row.negotiatedPaU = fromTotal.paU;
      row.negotiatedMarginPct = fromTotal.marginPct;
    }
  });
}

/**
 * Floor base of a minimum flat fee's purchase price, in m2 (setting `minFlatFeeM2`): the purchase price is
 * scaled up as if the piece were at least this big.
 * @returns The floor surface in m2.
 */
export function minFlatFeeM2(): number {
  return engineSettings().parameters.minFlatFeeM2;
}

/**
 * Actual purchase of a UPZ stock material — sets `_purchaseShare` on the lines.
 *
 * A genuinely purchased material has an invoiced cost, not an estimated one. That cost replaces the
 * reference estimate on the lines of that material, prorated on what each one consumes.
 *
 * Three explicit refusals, each leaving the estimate in place rather than inventing a figure: no purchase
 * entered for this material; unreadable format or unknown nature, so the need cannot be established;
 * missing price, so `finalCost` returns null (a half-entered purchase is not a 0 € purchase).
 *
 * The format is the one of the material's first line. Two lines of the same material with different
 * formats would describe two packagings, hence two purchases, which the per-material key cannot represent
 * today; the production summary still surfaces both, as it aggregates by material × workshop × format.
 * @param state The quote state.
 * @param res The quote result being built (purchase shares and purchase results are written on it).
 * @param margins The technical margins used for the need computation.
 */
function applyMaterialPurchases(state: QuoteState, res: QuoteResult, margins: TechnicalMargins): void {
  const purchases = state.materialPurchases ?? {};
  // Every UPZ material of the quote is grouped, purchased or not: the screen needs one row per material
  // so the price can be entered. Filtering on `purchases[ref]` would hide the very row to fill in. It is the
  // price entry that decides the effect, not the presence of a dictionary entry.
  const indexesByMaterial = new Map<string, number[]>();
  res.lines.forEach((row, i) => {
    const ref = row.matRef;
    if (!row.matPa?.isUpz || !ref) return;
    const indexes = indexesByMaterial.get(ref);
    if (indexes) indexes.push(i);
    else indexesByMaterial.set(ref, [i]);
  });
  for (const [ref, lineIndexes] of indexesByMaterial) {
    const firstRow = res.lines[lineIndexes[0]!]!;
    const nature = firstRow.matPa!.nature;
    if (nature !== "flexible" && nature !== "rigid") continue; // "none": no packaging
    const pieces: PurchasePiece[] = lineIndexes.map((i) => {
      const l = state.lines[i]!;
      return {
        L: num(l.L),
        H: num(l.H),
        qty: res.lines[i]!.quantity ?? 0,
        raw: neutralizedMargins(l.print, l.cut),
      };
    });
    const needInfo = needFromPieces(pieces, { nature, format: firstRow.formatRetained ?? null, coeff: firstRow.matPa!.coeff, margins: margins });
    if (!needInfo) continue;
    const cost = finalCost(purchases[ref], needInfo.need);
    // The material is published even without a price (`cout: null`), because the screen needs one row per
    // UPZ material to allow entry. `_purchaseShare` however is only set when the cost exists: without a
    // price the line keeps its reference estimate.
    if (cost != null) {
      const parts = allocateProRata(cost, needInfo.consumptionPerLine);
      lineIndexes.forEach((i, k) => {
        res.lines[i]!._purchaseShare = parts[k] ?? 0;
      });
    }
    (res.materialPurchaseResults ??= {})[ref] = {
      need: needInfo.need,
      cost: cost,
      balance: materialBalance(purchases[ref], needInfo.need),
      supplier: String(purchases[ref]?.supplier ?? "").trim() || null,
    };
  }
}

/**
 * Purchase price of one complete piece of UPZ material (gross price × the retained format's area).
 * @param lines The priced lines of a material.
 * @returns The price of a complete piece, or 0 when no UPZ line carries a gross price and a format.
 */
function pieceCompleteUpz(lines: readonly PricedLine[]): number {
  const row = lines.find((x) => x.matPa?.isUpz && x.matPa.gross != null && x.formatRetained);
  const dims = row ? parseFormat(row.formatRetained) : null;
  return dims ? round2(row!.matPa!.gross! * ((dims[0] * dims[1]) / 1e6)) : 0;
}

/**
 * Purchase price of a minimum flat fee. Two branches, discriminated by the only available fact: do we
 * know what it costs?
 *
 * 1. Purchase rate table available — take the observed cost, at the units it is really invoiced at: the
 *    workshop at its invoicing minimum, i.e. 1 m² per purchase group (already consolidated in Pass B),
 *    which applies per workshop; plus the material, on UPZ stock, at the complete piece. Outside UPZ
 *    stock the material is already in the workshop rate table (INCLUDED mode), so adding it would count twice.
 * 2. No rate table — no observed cost, so take the target purchase price, but on a 1 m² floor base.
 *    Without that floor a 0.25 m² piece would show a 95 % margin on a job that does not cover its costs,
 *    which is exactly what the minimum flat fee exists for.
 *
 * A flat fee's purchase price is therefore never zero, and it states where it comes from (`paOrigin`).
 * @param res The quote result whose minimum flat fee lines get their retained purchase price.
 */
function computeFlatFeePa(res: QuoteResult): void {
  for (const flatFeeLine of res.flatFeeLines) {
    const lines = res.lines.filter((row) => row._catKey === flatFeeLine.ref);
    // `servicePaM2` set = a rate table answered (supplier lookup, or workshop rate of a UPZ line).
    if (lines.some((row) => row.servicePaM2 != null)) {
      const workshop = lines.reduce((a, row) => a + (row.paServiceTot ?? 0), 0);
      flatFeeLine.paRetained = round2(workshop + pieceCompleteUpz(lines));
      flatFeeLine.paOrigin = "grid";
      continue;
    }
    const m2 = lines.reduce((a, row) => a + row.m2, 0);
    const target = lines.reduce((a, row) => a + (row._paGross ?? 0), 0);
    // The floor acts on the base, not on the amount: the area is raised to 1 m² and the same rate is
    // read again. A floor in euros would assume a €/m² price the material does not carry.
    flatFeeLine.paRetained = round2(m2 > 0 && m2 < minFlatFeeM2() ? target * (minFlatFeeM2() / m2) : target);
    flatFeeLine.paOrigin = "target";
  }
}

/** The three possible origins of the purchase price a line displays. The production table adds a fourth,
 *  "negotiated during production", which only exists after production starts — hence a distinct name so
 *  both can coexist in one file without shadowing each other. */
export type LinePaOrigin = "negotiated" | "grid" | "target";

/**
 * Origin of the purchase price a line displays — the single source of this classification, used both by
 * the banner breakdown and by the production table's origin column.
 *
 * "grid" does not strictly mean "supplier rate table": it means observed cost as opposed to targeted
 * cost. For a print line it is the workshop rate table (through `effectiveMarginM2`); for a stand, a transfer or
 * a custom-size extra it is the purchase price carried by the catalogue (`_paStand`).
 * @param row The priced line.
 * @returns The origin of its displayed purchase price.
 */
export function linePaOrigin(row: PricedLine): LinePaOrigin {
  if (row.paNegotiated) return "negotiated";
  if (row.effectiveMarginM2 != null || row._paStand != null) return "grid";
  return "target";
}

/**
 * Whether the line's workshop is invoiced under the Prodhuit (P8) minimum.
 * @param l The quote line.
 * @returns True for a P8 workshop.
 */
function isServiceKeyP8(l: QuoteLine): boolean {
  return SERVICE_KEY[l.workshopResp ?? ""] === "PRODHUIT";
}

/**
 * Margin coverage: aggregates the margin over the lines with a real purchase price only (a line falling back
 * to the target purchase price never counts as if it were real) and the displayed total purchase price.
 * @param res The quote result (its `marginCoverage` and the lines' displayed purchase price are set).
 */
function computeMarginCoverage(res: QuoteResult): void {
  let actualLineCount = 0, printLineCount = 0, actualNetPvSum = 0, actualPaSum = 0, weightedNetPv = 0, targetMarginPvSum = 0;
  let paTotal = 0, negotiatedCount = 0, actualCount = 0, targetCount = 0, negotiatedPaSum = 0, actualPaPart = 0, targetPaPart = 0;
  for (const row of res.lines) {
    if (row.targetMarginTier == null) {
      // Lines without a target-margin notion (stands etc.): still count their PA in folder totals.
      if (row.paMissing) continue;
      const pa = row.targetPa;
      if (pa == null) continue;
      row._displayedPa = pa;
      paTotal += pa;
      const origin = linePaOrigin(row);
      if (origin === "negotiated") { negotiatedCount++; negotiatedPaSum += pa; }
      else if (origin === "grid") { actualCount++; actualPaPart += pa; }
      else { targetCount++; targetPaPart += pa; }
      continue;
    }
    printLineCount++;
    const netPv = row.lineNetPv ?? 0;
    if (row._targetMarginTierFrac != null && netPv > 0) {
      weightedNetPv += netPv;
      targetMarginPvSum += row._targetMarginTierFrac * netPv;
    }
    // Actual purchase price present ⟺ effectiveMarginM2 is non-null (same gate: workshop rate table / negotiated PA / complete UPZ).
    const hasActualPa = row.effectiveMarginM2 != null && netPv > 0;
    // Displayed purchase price of the line: exactly what the cell shows — the retained actual/negotiated price
    // (net PV − effective margin €), else the target. `null` = line that cannot be priced (cell shown as "—",
    // e.g. missing dimensions): it is not aggregated as a zero target. A priced line is never empty.
    const linePa = hasActualPa ? round2(netPv - row.effectiveMarginM2! * row.m2) : row.m2 > 0 && row.targetPa != null ? row.targetPa : null;
    if (linePa != null) row._displayedPa = linePa;
    if (linePa != null) {
      paTotal += linePa;
      const origin = linePaOrigin(row);
      if (origin === "negotiated") { negotiatedCount++; negotiatedPaSum += linePa; }
      else if (origin === "grid") { actualCount++; actualPaPart += linePa; }
      else { targetCount++; targetPaPart += linePa; }
    }
    if (hasActualPa) {
      actualLineCount++;
      actualNetPvSum += netPv;
      actualPaSum += netPv - row.effectiveMarginM2! * row.m2;
    }
  }
  const servicePv = res.serviceItem.servicesTotal;
  const servicePa = res.serviceItem.servicesPaTotal;
  const fees = res.ancillaryCosts.total;
  // Mini forfaits: their PV is in the quote total, so their PA must be included too.
  let flatFeePaSum = 0;
  for (const flatFeeLine of res.flatFeeLines) {
    flatFeePaSum += flatFeeLine.paRetained;
    if (flatFeeLine.paOrigin === "grid") { actualCount++; actualPaPart += flatFeeLine.paRetained; }
    else { targetCount++; targetPaPart += flatFeeLine.paRetained; }
  }
  res.marginCoverage = {
    realLineCount: actualLineCount,
    printLineCount: printLineCount,
    actualNetPv: round2(actualNetPvSum + servicePv),
    actualPa: round2(actualPaSum + servicePa),
    weightedTargetMarginFrac: weightedNetPv > 0 ? targetMarginPvSum / weightedNetPv : null,
    paTotal: round2(paTotal + servicePa + fees + flatFeePaSum),
    breakdown: {
      negotiated: { n: negotiatedCount, pa: round2(negotiatedPaSum) },
      actual: { n: actualCount, pa: round2(actualPaPart + servicePa + fees) },
      target: { n: targetCount, pa: round2(targetPaPart) },
    },
  };
}

/**
 * Readable label of a line's purchase key, for blocker messages.
 * @param l The quote line.
 * @returns "material · print · cut", or an empty string when the line has none of them.
 */
function readableProcessKey(l: QuoteLine): string {
  return [l.material, l.print, l.cut].filter((x) => x && String(x).trim()).join(" · ");
}

/**
 * Evaluates the per-line UPZ validation blockers (missing distributor, brand, gross price, workshop or
 * workshop rate table). Only when the supply reference data is injected (supabase mode).
 * @param state The quote state.
 * @param res The quote result (each UPZ line gets its `upzBlockers`).
 * @param supplyRef The material supply reference data.
 */
export function evaluateUpzBlockers(state: QuoteState, res: QuoteResult, supplyRef?: MaterialSupplyRef): void {
  if (!supplyRef || Object.keys(supplyRef).length === 0) return;
  res.lines.forEach((row, i) => {
    if (!row.matPa?.isUpz) return;
    const l = state.lines[i]!;
    const supply = supplyRef[String(l.material ?? "")];
    const purchase = row._catKey ? state.materialPurchases?.[row._catKey] : undefined;
    const purchaseSupplier = String(purchase?.supplier ?? "").trim();
    const distributor = String(l.matSupplier ?? "").trim() || String(supply?.supplier ?? "").trim() || purchaseSupplier;
    const brand = String(l.brand ?? "").trim() || String(supply?.brand ?? "").trim();
    const grossPresent = !row.matPa.grossMissing || num(purchase?.pricePerM2) > 0;
    const workshopMissing = String(l.workshopResp ?? "").trim() === "";
    const workmanshipMissing = !workshopMissing && manualPaM2Of(l, row.m2) == null && !row._paWorkshopIsActual;
    row.upzBlockers = {
      distributor: distributor === "",
      brand: brand === "",
      gross: !grossPresent,
      workshop: workshopMissing,
      workmanship: workmanshipMissing,
      ...(workmanshipMissing ? { processKey: readableProcessKey(l) } : {}),
    };
  });
}

/**
 * Builds the blocker messages (completeness, stale override, sleeve, UPZ stock, custom size).
 * @param state The quote state.
 * @param res The computed quote result.
 * @param batch True when pricing a batch of lines: the quote-level services-block blockers are skipped.
 * @returns The blocker messages to display.
 */
function buildBlockers(state: QuoteState, res: QuoteResult, batch: boolean): string[] {
  const lines = state.lines;
  const blockers: string[] = [];
  const displayNumbers = displayNumMap(lines);
  /**
   * Display number of a line.
   * @param id The line id.
   * @returns The displayed number, or an empty string.
   */
  const displayNumberOf = (id?: number) => (id != null ? displayNumbers.get(id) ?? "" : "");

  const paMissingCount = res.lines.filter((r) => r.paMissing).length;
  if (paMissingCount > 0) blockers.push(engineMessage("engineBlockerPaMissing", { count: paMissingCount }));

  state.lines.forEach((l) => {
    if (hasUnknownMaterialGroup(l.material)) {
      blockers.push(engineMessage("engineBlockerUnknownMaterialGroup", { line: displayNumberOf(l.id), material: String(l.material) }));
    }
  });
  for (const row of res.lines) {
    if (row.missingFields && row.missingFields.length) {
      blockers.push(engineMessage("engineBlockerFieldsRequired", { line: displayNumberOf(row.id), fields: messageListParam(row.missingFields.map(fieldItem)) }));
    }
    if (row.incompatibleFields && row.incompatibleFields.length) {
      const items = row.incompatibleFields.map((c) => engineMessage("engineIncompatibleItem", { ref: c.ref, field: messageListParam([fieldItem(c.field)]) }));
      blockers.push(engineMessage("engineBlockerFieldsIncompatible", { line: displayNumberOf(row.id), fields: messageListParam(items) }));
    }
    // UPZ validation blockers: `row.upzBlockers` is set by `evaluateUpzBlockers` only when the supply reference
    // data is injected (supabase mode). In degraded (embedded) mode there are none and the freeze-time guard
    // (app side) takes over.
    const upz = row.upzBlockers;
    if (upz) {
      const n = displayNumberOf(row.id);
      const missingItems: string[] = [];
      if (upz.distributor) missingItems.push("engineUpzMissingDistributor");
      if (upz.brand) missingItems.push("engineUpzMissingBrand");
      if (upz.gross) missingItems.push("engineUpzMissingGrossPa");
      if (missingItems.length) blockers.push(engineMessage("engineBlockerUpzPurchase", { line: n, items: messageListParam(missingItems) }));
      if (upz.workshop) blockers.push(engineMessage("engineBlockerUpzWorkshop", { line: n }));
      if (upz.workmanship) {
        blockers.push(
          upz.processKey
            ? engineMessage("engineBlockerUpzNoPaGrid", { line: n, processKey: upz.processKey })
            : engineMessage("engineBlockerUpzNoPaGridAnyKey", { line: n }),
        );
      }
    }
  }
  for (const p of res.serviceItem.lines) if (p.blocked) blockers.push(p.blocked);
  const servicesBlock = state.services ?? {};
  const hasPrintLines = lines.some(
    (l) => (isLargeFormatPrintRegime(l) || isTierRegime(l) || isCustomPrintRegime(l) || isUnitRegime(l)) && (l.material || l.ref || l.offCatalogue),
  );
  if (hasPrintLines && !batch) {
    if (!servicesBlock.infoMode || servicesBlock.infoMode === "off") blockers.push("engineBlockerInfoRequired");
    if (!servicesBlock.packagingMode || servicesBlock.packagingMode === "off") blockers.push("engineBlockerPackagingRequired");
    if (!(servicesBlock.logistics ?? []).some((l) => l.code && num(l.quantity) > 0)) blockers.push("engineBlockerDeliveryRequired");
  }
  for (const row of res.lines) {
    if (row.forceStale) {
      blockers.push(engineMessage("engineBlockerForceStale", { line: displayNumberOf(row.id) }));
    }
  }
  for (const l of lines) {
    if (sleeveWithoutDiameter(l)) {
      blockers.push(engineMessage("engineBlockerSleeveDiameter", { line: displayNumberOf(l.id) }));
    }
  }
  const standCatalogue = [...STANDS, ...FINISHED_PRODUCTS];
  for (const l of lines) {
    if (!isCustomSizeSurcharge(l) || !l.customSizeFinal) continue;
    const parentLine = lines.find((m) => m.id === l.linkId);
    const parentStand = parentLine ? findStand(String(parentLine.ref ?? "")) : undefined;
    const customSizeRate = parentStand ? customSizeRateForStand(parentStand) : null;
    if (!parentStand || !customSizeRate || !(parentStand.L > 0) || !(parentStand.H > 0)) continue;
    const refusals = validateCustomSize({
      base: { L: parentStand.L, H: parentStand.H },
      final: l.customSizeFinal,
      rate: customSizeRate,
      pvParentTier1: num(parentStand.pv_tier1),
      candidates: standCatalogue.filter(
        (s) => s.component === parentStand.component && (s.faces ?? 1) === (parentStand.faces ?? 1),
      ),
    });
    for (const r of refusals) {
      blockers.push(engineMessage("engineBlockerLineReason", { line: displayNumberOf(l.id), reason: messageParam(r.message) }));
    }
  }
  return blockers;
}

// ————————————————————————————————————————————————————————————————————————————————————————————————
// GENERAL DISCOUNT IN TARGET-PRICE MODE (book 4.10 §5)
// ————————————————————————————————————————————————————————————————————————————————————————————————

/** Discount base context, settled by the cascade before the resolution is called. */
interface TargetContext {
  discountBase: number;
  maxDiscount: number;
  rush: RushTarget | undefined;
  excludedCount: number;
}

/**
 * Resolves the general discount amount from the targeted final total, and builds the banner info.
 * @param state The quote state.
 * @param res The quote result being built.
 * @param ctx The discount base context.
 * @returns The target-mode info (frozen amount, recalibrated amount, refusal, floor…).
 */
function resolveDiscountTarget(state: QuoteState, res: QuoteResult, ctx: TargetContext): GeneralDiscountTargetInfo {
  const target = round2(num(state.discountGenTarget));
  // Target is the client-facing total (`res.totalBeforeDiscount`), excluded lines included.
  const entry: TargetEntry = {
    totalBeforeDiscount: res.totalBeforeDiscount, maxDiscount: ctx.maxDiscount, target,
    ...(ctx.rush ? { rush: ctx.rush } : {}),
  };
  const floorAmount = quoteFloor(entry);
  const info: GeneralDiscountTargetInfo = {
    target,
    ...(state.discountGenLabel ? { label: state.discountGenLabel } : {}),
    ...(state.discountGenModifiedAt ? { modifiedAt: state.discountGenModifiedAt } : {}),
    ...(state.discountGenModifiedBy ? { modifiedBy: state.discountGenModifiedBy } : {}),
    amountFrozen: null, recalibratedAmount: null, floorAmount, gap: 0, belowPa: [], excludedCount: ctx.excludedCount,
    totalBeforeDiscount: res.totalBeforeDiscount, maxDiscount: ctx.maxDiscount, ...(ctx.rush ? { rush: ctx.rush } : {}),
  };
  if (!(target > 0)) return info;

  const r = resolveTargetPrice(entry);
  if (!r.ok) {
    info.refusal = refusalMessage(r);
    const frozen = isFilled(state.frozenGeneralDiscountAmount) ? round2(num(state.frozenGeneralDiscountAmount)) : null;
    info.amountFrozen = frozen;
    return info;
  }
  info.recalibratedAmount = r.amount;
  if (r.roundedByRush) info.rushRounded = true;
  info.amountFrozen = isFilled(state.frozenGeneralDiscountAmount) ? round2(num(state.frozenGeneralDiscountAmount)) : r.amount;
  return info;
}

/**
 * Finalizes the target-mode info once the final total is known: gap, drift reason and the internal
 * split of the discount across the lines.
 * @param info The target-mode info to complete.
 * @param totalFinal The final total excl. VAT, rush included.
 * @param state The quote state.
 * @param res The quote result (lines receive their conceded share).
 * @param isExcluded Whether a line (by index) is out of the discount base.
 */
function finalizeTargetGap(
  info: GeneralDiscountTargetInfo,
  totalFinal: number,
  state: QuoteState,
  res: QuoteResult,
  isExcluded: (i: number) => boolean,
): void {
  info.gap = round2(totalFinal - info.target);
  if (info.target > 0 && info.gap !== 0 && !info.refusal) {
    info.driftReason = info.rushRounded && Math.abs(info.gap) <= 0.01
      ? "engineDriftRushRounded"
      : info.gap > 0
        ? "engineDriftQuoteGrew"
        : "engineDriftQuoteShrank";
  }

  // Internal line split only — total stays driven by the global amount.
  const amount = info.amountFrozen ?? 0;
  if (amount <= 0) return;
  const entries: AllocationEntry[] = [];
  const index: number[] = [];
  res.lines.forEach((row, i) => {
    if (isExcluded(i) || row.flatFeeReplaced || !(row.total > 0)) return;
    entries.push({
      key: String(state.lines[i]?.num ?? i + 1),
      ...(state.lines[i]?.fileName ? { label: state.lines[i]!.fileName! } : {}),
      total: row.total,
      ...(row._floorTotal != null ? { floorTotal: row._floorTotal } : {}),
      ...(row._displayedPa != null ? { pa: row._displayedPa } : {}),
    });
    index.push(i);
  });
  const allocation = allocateDiscount(entries, amount, state.includeMinima ?? false);
  info.belowPa = allocation.belowPa;
  index.forEach((i, k) => {
    const row = res.lines[i];
    if (!row) return;
    row.discountGenConceded = allocation.concessions[k] ?? 0;
    row.discountGenRateEff = effectiveRate(row.total, allocation.concessions[k] ?? 0);
  });
}

/**
 * Computes the quote margin and applies the margin thresholds: alert below the target, blocker below the floor.
 * @param res The quote result (margin, alert and blockers are set on it).
 * @param thresholds The injected thresholds; absent = no guard.
 */
function applyMarginThresholds(res: QuoteResult, thresholds: { targetPct: number; floorPct: number } | undefined): void {
  const finalTotal = res.totalFinal;
  if (!(finalTotal > 0)) { res.quoteMarginPct = null; return; }
  const quoteMargin = round2(((finalTotal - res.marginCoverage.paTotal) / finalTotal) * 100);
  res.quoteMarginPct = quoteMargin;
  if (!thresholds) return;

  // Percentages are raw numbers (`...Pct` parameters): the display layer formats them with one decimal for its locale.
  if (quoteMargin < thresholds.floorPct) {
    res.blockers.push(engineMessage("engineBlockerMarginBelowFloor", { marginPct: quoteMargin, floorPct: thresholds.floorPct }));
  } else if (quoteMargin < thresholds.targetPct) {
    res.marginAlert = engineMessage("engineMarginBelowTarget", { marginPct: quoteMargin, targetPct: thresholds.targetPct });
  }
}
